import { createSlice } from '@reduxjs/toolkit';
import clientService from '../services/clientService';
import userService from '../services/userService';
import {
  Client,
  ClientAddress,
  ClientAdmin,
  ClientContact,
  ClientListItem,
  ClientMemberContact,
} from './models/client.model';
import * as uiSlice from './ui.slice';
import {
  Program,
  ServicePlan,
  ServicePlanList,
  ServicePlanServiceArea,
} from './models/client-program.model';
import { PassengerClientData } from './models/passenger-client-data.model';
import {
  ClientTransportationBenefit,
  TransportationBenefitModel,
  TransportationBenefitModelState,
} from './models/transportation-benefit-model.model';
import { TransportationBenefitFunction } from './models/transportation-benefit-function.model';
import { TransportationBenefit } from './models/transportation-benefit.model';
import {
  CreatableNamedLocation,
  DeleteNamedLocationRequest,
  EditableNamedLocation,
  NamedLocation,
} from './models/named-location.model';
import {
  ClientLocation,
  DeleteClientLocationRequest,
} from './models/client-location.model';
import { ClientSetting } from './models/client-settings.model';
import { PagedList } from './models/paged-list.model';
import { ClientTransporationService } from './models/transportation-service.model';
import { ClientPurposeCode } from './models/purpose-code-model.model';
import { forkJoin } from 'rxjs';
import { ClientAdminSettings } from './models/client-admin-settings.model';

/**
 * Cache key for the member-contact maps: the contact is fetched per
 * clientBC + servicePlanID (the "Member Contact Phone Number" setting can be
 * plan-scoped on the back end).
 */
export const memberContactKey = (clientBC: string, servicePlanID: string) =>
  `${clientBC}:${servicePlanID}`;

interface ClientState {
  // Clients List
  clientsList: PagedList<ClientListItem>;
  loadingClientsList: boolean;
  loadedClientsList: boolean;

  // Clients
  clients: Client[];
  loadingClients: boolean;
  loadedClients: boolean;
  currentClient: Client | null;
  loadingCurrentClient: boolean;
  loadedCurrentClient: boolean;
  savingClient: boolean;
  savedClient: boolean;

  // Addresses
  currentClientAddresses: ClientAddress[];
  loadingAddresses: boolean;
  loadedAddresses: boolean;
  savingAddress: boolean;
  savedAddress: boolean;

  // Contacts
  currentClientContacts: ClientContact[];
  loadingContacts: boolean;
  loadedContacts: boolean;
  savingContact: boolean;
  savedContact: boolean;

  // Member contact (client name + "Member Contact Phone Number" setting,
  // interpolated into member-facing validation messages). Keyed by
  // clientBC + servicePlanID (see `memberContactKey`) — the setting resolves
  // per service plan, and keying both ways keeps admin flows spanning several
  // clients/plans from clobbering each other; a null entry records a settled
  // miss (failed/empty lookup) so consumers fall back to the portal's generic
  // support strings instead of refetching.
  memberContactByClientPlan: {
    [clientPlanKey: string]: ClientMemberContact | null;
  };
  loadingMemberContactByClientPlan: { [clientPlanKey: string]: boolean };

  // Admins
  currentClientAdmins: PagedList<ClientAdmin>;
  clientAdminsByClient: { [clientBC: string]: PagedList<ClientAdmin> };
  loadingAdmins: boolean;
  loadedAdmins: boolean;
  savingAdmin: boolean;
  savedAdmin: boolean;
  adminError: string | null;

  // Purpose Codes
  currentClientPurposeCodes: ClientPurposeCode[];
  loadingPurposeCodes: boolean;
  loadedPurposeCodes: boolean;
  savingPurposeCode: boolean;
  savedPurposeCode: boolean;

  // Service Areeas
  currentClientServiceAreas: ServicePlanServiceArea[];
  loadingServiceAreas: boolean;
  loadedServiceAreas: boolean;
  savingServiceArea: boolean;
  savedServiceArea: boolean;

  // Transportation Benefits
  currentTransportationBenefits: TransportationBenefit[];
  loadingTransportationBenefits: boolean;
  loadedTransportationBenefits: boolean;
  savingTransportationBenefit: boolean;
  savedTransportationBenefit: boolean;

  // Service Plans
  servicePlanList: ServicePlanList | null;
  manualServicePlanList: ServicePlanList | null;
  loadingServicePlans: boolean;
  loadedServicePlans: boolean;
  savingServicePlan: boolean;
  savedServicePlan: boolean;

  // Programs
  programs: Program[];
  loadingPrograms: boolean;
  loadedPrograms: boolean;
  savingProgram: boolean;
  savedProgram: boolean;

  // Passenger Client Data
  passengerClientData: PassengerClientData;
  loadingPassengerClientData: boolean;
  loadedPassengerClientData: boolean;

  // Transportation Benefit Models
  allTransportationBenefitModels: TransportationBenefitModel[];
  activeTransportationBenefitModels: TransportationBenefitModel[];
  loadingTransportationBenefitModels: boolean;
  loadedTransportationBenefitModels: boolean;
  savingTransportationBenefitModel: boolean;
  savedTransportationBenefitModel: boolean;

  // Transportation Benefit Functions
  transportationBenefitFunctions: TransportationBenefitFunction[];
  loadingTransportationBenefitFunctions: boolean;
  loadedTransportationBenefitFunctions: boolean;

  // Client Profile Program Settings
  programSettingsById: any;
  loadingProgramSettings: boolean;
  loadedProgramSettings: boolean;
  savingProgramSetting: boolean;
  savedProgramSetting: boolean;

  // Named Locations
  allNamedLocations: NamedLocation[];
  loadingNamedLocations: boolean;
  loadedNamedLocations: boolean;
  savingNamedLocation: boolean;
  savedNamedLocation: boolean;

  // Client Locations
  allClientLocations: ClientLocation[];
  loadingClientLocations: boolean;
  loadedClientLocations: boolean;
  savingClientLocation: boolean;
  savedClientLocation: boolean;

  // Client Preferences
  clientSettings: ClientSetting[] | null;
  loadingClientSettings: boolean;
  loadedClientSettings: boolean;
  savingClientSettings: boolean;
  savedClientSettings: boolean;

  // Client models
  savingClientModels: boolean;
  savedClientModels: boolean;

  //Client Admin Settings
  clientAdminSettings: ClientAdminSettings | null;
}

export const initialState: ClientState = {
  // Clients List
  clientsList: {
    itemCount: 0,
    currentPage: 0,
    totalPages: 0,
    items: [],
  },
  loadingClientsList: false,
  loadedClientsList: false,

  // Clients
  clients: [],
  loadingClients: false,
  loadedClients: false,
  currentClient: null,
  loadingCurrentClient: false,
  loadedCurrentClient: false,
  savingClient: false,
  savedClient: false,

  // Addresses
  currentClientAddresses: [],
  loadingAddresses: false,
  loadedAddresses: false,
  savingAddress: false,
  savedAddress: false,

  // Contacts
  currentClientContacts: [],
  loadingContacts: false,
  loadedContacts: false,
  savingContact: false,
  savedContact: false,

  // Member contact
  memberContactByClientPlan: {},
  loadingMemberContactByClientPlan: {},

  // Admins
  currentClientAdmins: {
    itemCount: 0,
    currentPage: 0,
    totalPages: 0,
    items: [],
  },
  clientAdminsByClient: {},
  loadingAdmins: false,
  loadedAdmins: false,
  savingAdmin: false,
  savedAdmin: false,
  adminError: null,

  // Purpose Codes
  currentClientPurposeCodes: [],
  loadingPurposeCodes: false,
  loadedPurposeCodes: false,
  savingPurposeCode: false,
  savedPurposeCode: false,

  // Service Areas
  currentClientServiceAreas: [],
  loadingServiceAreas: false,
  loadedServiceAreas: false,
  savingServiceArea: false,
  savedServiceArea: false,

  // Transportation Benefits
  currentTransportationBenefits: [],
  loadingTransportationBenefits: false,
  loadedTransportationBenefits: false,
  savingTransportationBenefit: false,
  savedTransportationBenefit: false,

  // Service Plans
  servicePlanList: null,
  manualServicePlanList: null,
  loadingServicePlans: false,
  loadedServicePlans: false,
  savingServicePlan: false,
  savedServicePlan: false,

  // Programs
  programs: [],
  loadingPrograms: false,
  loadedPrograms: false,
  savingProgram: false,
  savedProgram: false,

  // Passenger Client Data
  passengerClientData: { purposeCodes: [], servicePlans: [] },
  loadingPassengerClientData: false,
  loadedPassengerClientData: false,

  // Transportation Benefit Models
  allTransportationBenefitModels: [],
  activeTransportationBenefitModels: [],
  loadingTransportationBenefitModels: false,
  loadedTransportationBenefitModels: false,
  savingTransportationBenefitModel: false,
  savedTransportationBenefitModel: false,

  // Transportation Benefit Functions
  transportationBenefitFunctions: [],
  loadingTransportationBenefitFunctions: false,
  loadedTransportationBenefitFunctions: false,

  // Client Profile Program Settings
  programSettingsById: {},
  loadingProgramSettings: false,
  loadedProgramSettings: false,
  savingProgramSetting: false,
  savedProgramSetting: false,

  // Named Locations
  allNamedLocations: [],
  loadingNamedLocations: false,
  loadedNamedLocations: false,
  savingNamedLocation: false,
  savedNamedLocation: false,

  // Client Locations
  allClientLocations: [],
  loadingClientLocations: false,
  loadedClientLocations: false,
  savingClientLocation: false,
  savedClientLocation: false,

  // Client Settings
  clientSettings: null,
  loadingClientSettings: false,
  loadedClientSettings: false,
  savingClientSettings: false,
  savedClientSettings: false,

  // Client models
  savingClientModels: false,
  savedClientModels: false,

  //Client Admin Settings
  clientAdminSettings: null,
};

export const slice = createSlice({
  name: 'client',
  initialState,
  reducers: {
    // Clients List
    setClientsList: (
      state: ClientState,
      action: { type: string; payload: PagedList<ClientListItem> },
    ) => {
      state.clientsList = action.payload;
    },
    setLoadingClientsList: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingClientsList = action.payload;
    },
    setLoadedClientsList: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedClientsList = action.payload;
    },

    // Clients
    setClients: (
      state: ClientState,
      action: { type: string; payload: Client[] },
    ) => {
      state.clients = action.payload;
    },
    setLoadingClients: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingClients = action.payload;
    },
    setLoadedClients: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedClients = action.payload;
    },
    setCurrentClient: (
      state: ClientState,
      action: { type: string; payload: Client | null },
    ) => {
      state.currentClient = action.payload;
    },
    resetCurrentClientInfo: (state: ClientState) => {
      state.currentClient = null;
      state.currentClientAddresses = [];
      state.currentClientContacts = [];
    },
    setLoadingCurrentClient: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingCurrentClient = action.payload;
    },
    setLoadedCurrentClient: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedCurrentClient = action.payload;
    },
    addClient: (
      state: ClientState,
      action: { type: string; payload: Client },
    ) => {
      state.clients.push(action.payload);
      state.currentClient = action.payload;
    },
    updateClient: (
      state: ClientState,
      action: { type: string; payload: Client },
    ) => {
      const clients = [...state.clients];
      const idx = clients.findIndex(
        (c) => c.clientBC === action.payload.clientBC,
      );
      if (idx >= 0) {
        clients.splice(idx, 1, action.payload);
        state.clients = clients;
      }
      state.currentClient = action.payload;
    },
    deleteClient: (
      state: ClientState,
      action: { type: string; payload: string },
    ) => {
      const clients = state.clients.filter(
        (c) => c.clientBC !== action.payload,
      );
      state.clients = clients;
    },
    addAddress: (
      state: ClientState,
      action: {
        type: string;
        payload: { clientBC: string; address: ClientAddress };
      },
    ) => {
      state.currentClientAddresses.push(action.payload.address);
    },
    updateAddress: (
      state: ClientState,
      action: {
        type: string;
        payload: { clientBC: string; address: ClientAddress };
      },
    ) => {
      const addresses = state.currentClientAddresses || [];
      const idx = addresses.findIndex(
        (addr) =>
          addr.clientAddressUUID === action.payload.address.clientAddressUUID,
      );
      if (idx >= 0) {
        addresses.splice(idx, 1, action.payload.address);
        state.currentClientAddresses = addresses;
      }
    },
    deleteAddress: (
      state: ClientState,
      action: {
        type: string;
        payload: { clientBC: string; addressUUID: string };
      },
    ) => {
      const addresses = state.currentClientAddresses || [];
      const idx = addresses.findIndex(
        (addr) => addr.clientAddressUUID === action.payload.addressUUID,
      );
      if (idx >= 0) {
        addresses.splice(idx, 1);
        state.currentClientAddresses = addresses;
      }
    },
    setSavingClient: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingClient = action.payload;
    },
    setSavedClient: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedClient = action.payload;
    },

    // Addresses
    setAddresses: (
      state: ClientState,
      action: { type: string; payload: ClientAddress[] },
    ) => {
      state.currentClientAddresses = action.payload;
    },
    setLoadingAddresses: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingAddresses = action.payload;
    },
    setLoadedAddresses: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedAddresses = action.payload;
    },
    setSavingAddress: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingAddress = action.payload;
    },
    setSavedAddress: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedAddress = action.payload;
    },

    // Contacts
    setContacts: (
      state: ClientState,
      action: { type: string; payload: ClientContact[] },
    ) => {
      state.currentClientContacts = action.payload;
    },
    setLoadingContacts: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingContacts = action.payload;
    },
    setLoadedContacts: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedContacts = action.payload;
    },
    setMemberContact: (
      state: ClientState,
      action: {
        type: string;
        payload: {
          clientBC: string;
          servicePlanID: string;
          contact: ClientMemberContact | null;
        };
      },
    ) => {
      state.memberContactByClientPlan[
        memberContactKey(action.payload.clientBC, action.payload.servicePlanID)
      ] = action.payload.contact;
    },
    setLoadingMemberContact: (
      state: ClientState,
      action: {
        type: string;
        payload: { clientBC: string; servicePlanID: string; loading: boolean };
      },
    ) => {
      state.loadingMemberContactByClientPlan[
        memberContactKey(action.payload.clientBC, action.payload.servicePlanID)
      ] = action.payload.loading;
    },
    setSavingContact: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingContact = action.payload;
    },
    addContact: (
      state: ClientState,
      action: {
        type: string;
        payload: { clientBC: string; contact: ClientContact };
      },
    ) => {
      state.currentClientContacts.push(action.payload.contact);
    },
    updateContact: (
      state: ClientState,
      action: {
        type: string;
        payload: { clientBC: string; contact: ClientContact };
      },
    ) => {
      const contacts = state.currentClientContacts || [];
      const idx = contacts.findIndex(
        (addr) =>
          addr.clientContactUUID === action.payload.contact.clientContactUUID,
      );
      if (idx >= 0) {
        contacts.splice(idx, 1, action.payload.contact);
        state.currentClientContacts = contacts;
      }
    },
    deleteContact: (
      state: ClientState,
      action: {
        type: string;
        payload: { clientBC: string; contactUUID: string };
      },
    ) => {
      const contacts = state.currentClientContacts || [];
      const idx = contacts.findIndex(
        (addr) => addr.clientContactUUID === action.payload.contactUUID,
      );
      if (idx >= 0) {
        contacts.splice(idx, 1);
        state.currentClientContacts = contacts;
      }
    },
    setSavedContact: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedContact = action.payload;
    },

    // Admins
    setAdmins: (
      state: ClientState,
      action: { type: string; payload: PagedList<ClientAdmin> },
    ) => {
      state.currentClientAdmins = action.payload;
    },
    setClientAdmins: (
      state: ClientState,
      action: {
        type: string;
        payload: { clientBC: string; admins: PagedList<ClientAdmin> };
      },
    ) => {
      state.clientAdminsByClient[action.payload.clientBC] =
        action.payload.admins;
      state.currentClientAdmins = action.payload.admins;
    },
    addClientAdmin: (
      state: ClientState,
      action: {
        type: string;
        payload: { clientBC: string; admin: ClientAdmin };
      },
    ) => {
      const { clientBC, admin } = action.payload;
      if (!state.clientAdminsByClient[clientBC]) {
        state.clientAdminsByClient[clientBC] = {
          itemCount: 0,
          currentPage: 1,
          totalPages: 1,
          items: [],
        };
        state.currentClientAdmins = {
          itemCount: 0,
          currentPage: 1,
          totalPages: 1,
          items: [],
        };
      }
      state.clientAdminsByClient[clientBC].items.unshift(admin);
      state.clientAdminsByClient[clientBC].itemCount++;
      state.currentClientAdmins.items.unshift(admin);
      state.currentClientAdmins.itemCount++;
    },
    deleteClientAdmin: (
      state: ClientState,
      action: {
        type: string;
        payload: { clientBC: string; userAccountID: number };
      },
    ) => {
      const { clientBC, userAccountID } = action.payload;
      const admins = state.clientAdminsByClient[clientBC]?.items || [];
      const idx = admins.findIndex((a) => a.userAccountID === userAccountID);
      if (idx >= 0) {
        admins.splice(idx, 1);
        state.clientAdminsByClient[clientBC].items = admins;
        state.clientAdminsByClient[clientBC].itemCount--;
        state.currentClientAdmins.items = admins;
        state.currentClientAdmins.itemCount--;
      }
    },

    setLoadingAdmins: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingAdmins = action.payload;
    },
    setLoadedAdmins: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedAdmins = action.payload;
    },
    setSavingAdmin: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingAdmin = action.payload;
    },
    setSavedAdmin: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedAdmin = action.payload;
    },
    setAdminError: (
      state: ClientState,
      action: { type: string; payload: string | null },
    ) => {
      state.adminError = action.payload;
    },

    // Purpose codes
    setPurposeCodes: (
      state: ClientState,
      action: { type: string; payload: ClientPurposeCode[] },
    ) => {
      state.currentClientPurposeCodes = action.payload;
    },
    setLoadingPurposeCodes: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingPurposeCodes = action.payload;
    },
    setLoadedPurposeCodes: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedPurposeCodes = action.payload;
    },
    addPurposeCode: (
      state: ClientState,
      action: {
        type: string;
        payload: ClientPurposeCode;
      },
    ) => {
      const purposeCodes = [...state.currentClientPurposeCodes];
      purposeCodes.push(action.payload);
      purposeCodes.sort((a, b) => a.name.localeCompare(b.name));
      state.currentClientPurposeCodes = purposeCodes;
    },
    updatePurposeCode: (
      state: ClientState,
      action: {
        type: string;
        payload: ClientPurposeCode;
      },
    ) => {
      const purposeCodes = [...state.currentClientPurposeCodes];
      const idx = purposeCodes.findIndex(
        (pc) => pc.purposeCodeUUID === action.payload.purposeCodeUUID,
      );
      if (idx >= 0) {
        purposeCodes.splice(idx, 1, action.payload);
        state.currentClientPurposeCodes = purposeCodes;
      }
    },
    deletePurposeCode: (
      state: ClientState,
      action: {
        type: string;
        payload: ClientPurposeCode;
      },
    ) => {
      const purposeCodes = [...state.currentClientPurposeCodes];
      const idx = purposeCodes.findIndex(
        (pc) => pc.purposeCodeUUID === action.payload.purposeCodeUUID,
      );
      if (idx >= 0) {
        purposeCodes.splice(idx, 1);
        state.currentClientPurposeCodes = purposeCodes;
      }
    },
    assignPurposeCode: (
      state: ClientState,
      action: {
        type: string;
        payload: { servicePlanUUID: string; purposeCodeUUID: string };
      },
    ) => {
      const servicePlan = state.servicePlanList!.servicePlans.find(
        (p) => p.servicePlanUUID === action.payload.servicePlanUUID,
      );
      if (servicePlan) {
        servicePlan.purposeCodeUUIDs?.push(action.payload.purposeCodeUUID);
      }
    },
    unassignPurposeCode: (
      state: ClientState,
      action: {
        type: string;
        payload: { servicePlanUUID: string; purposeCodeUUID: string };
      },
    ) => {
      const servicePlan = state.servicePlanList!.servicePlans.find(
        (p) => p.servicePlanUUID === action.payload.servicePlanUUID,
      );
      const idx = servicePlan?.purposeCodeUUIDs?.findIndex(
        (pcUUID) => pcUUID === action.payload.purposeCodeUUID,
      );
      if (idx !== undefined && idx >= 0) {
        servicePlan?.purposeCodeUUIDs?.splice(idx, 1);
      }
    },
    setSavingPurposeCode: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingPurposeCode = action.payload;
    },
    setSavedPurposeCode: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedPurposeCode = action.payload;
    },

    // Service areas
    setServiceAreas: (
      state: ClientState,
      action: { type: string; payload: ServicePlanServiceArea[] },
    ) => {
      state.currentClientServiceAreas = action.payload;
    },
    setLoadingServiceAreas: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingServiceAreas = action.payload;
    },
    setLoadedServiceAreas: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedServiceAreas = action.payload;
    },
    addServiceArea: (
      state: ClientState,
      action: {
        type: string;
        payload: ServicePlanServiceArea;
      },
    ) => {
      const serviceAreas = [...state.currentClientServiceAreas];
      serviceAreas.push(action.payload);
      serviceAreas.sort((a, b) => a.name.localeCompare(b.name));
      state.currentClientServiceAreas = serviceAreas;
    },
    updateServiceArea: (
      state: ClientState,
      action: {
        type: string;
        payload: ServicePlanServiceArea;
      },
    ) => {
      const serviceAreas = [...state.currentClientServiceAreas];
      const idx = serviceAreas.findIndex(
        (pc) => pc.serviceAreaUUID === action.payload.serviceAreaUUID,
      );
      if (idx >= 0) {
        serviceAreas.splice(idx, 1, action.payload);
        state.currentClientServiceAreas = serviceAreas;
      }
    },
    deleteServiceArea: (
      state: ClientState,
      action: {
        type: string;
        payload: ServicePlanServiceArea;
      },
    ) => {
      const serviceAreas = [...state.currentClientServiceAreas];
      const idx = serviceAreas.findIndex(
        (pc) => pc.serviceAreaUUID === action.payload.serviceAreaUUID,
      );
      if (idx >= 0) {
        serviceAreas.splice(idx, 1);
        state.currentClientServiceAreas = serviceAreas;
      }
    },
    assignServiceArea: (
      state: ClientState,
      action: {
        type: string;
        payload: { servicePlanUUID: string; serviceAreaUUID: string };
      },
    ) => {
      const servicePlan = state.servicePlanList!.servicePlans.find(
        (p) => p.servicePlanUUID === action.payload.servicePlanUUID,
      );
      if (servicePlan) {
        servicePlan.serviceAreaUUIDs?.push(action.payload.serviceAreaUUID);
      }
    },
    unassignServiceArea: (
      state: ClientState,
      action: {
        type: string;
        payload: { servicePlanUUID: string; serviceAreaUUID: string };
      },
    ) => {
      const servicePlan = state.servicePlanList!.servicePlans.find(
        (p) => p.servicePlanUUID === action.payload.servicePlanUUID,
      );
      const idx = servicePlan?.serviceAreaUUIDs?.findIndex(
        (saUUID) => saUUID === action.payload.serviceAreaUUID,
      );
      if (idx !== undefined && idx >= 0) {
        servicePlan?.serviceAreaUUIDs?.splice(idx, 1);
      }
    },
    setSavingServiceArea: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingServiceArea = action.payload;
    },
    setSavedServiceArea: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedServiceArea = action.payload;
    },

    // Transportation benefits
    setTransportationBenefits: (
      state: ClientState,
      action: { type: string; payload: TransportationBenefit[] },
    ) => {
      state.currentTransportationBenefits = action.payload;
    },
    setLoadingTransportationBenefits: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingTransportationBenefits = action.payload;
    },
    setLoadedTransportationBenefits: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedTransportationBenefits = action.payload;
    },
    createTransportationBenefit: (
      state: ClientState,
      action: {
        type: string;
        payload: TransportationBenefit;
      },
    ) => {
      const transportationBenefits = [...state.currentTransportationBenefits];
      transportationBenefits.push(action.payload);
      transportationBenefits.sort((a, b) => a.name.localeCompare(b.name));
      state.currentTransportationBenefits = transportationBenefits;
    },
    addTransportationBenefit: (
      state: ClientState,
      action: {
        type: string;
        payload: TransportationBenefit;
      },
    ) => {
      const serviceAreas = [...state.currentTransportationBenefits];
      serviceAreas.push(action.payload);
      serviceAreas.sort((a, b) => a.name.localeCompare(b.name));
      state.currentTransportationBenefits = serviceAreas;
    },
    updateTransportationBenefit: (
      state: ClientState,
      action: {
        type: string;
        payload: TransportationBenefit;
      },
    ) => {
      const transportationBenefits = [...state.currentTransportationBenefits];
      const idx = transportationBenefits.findIndex(
        (tp) =>
          tp.transportationBenefitUUID ===
          action.payload.transportationBenefitUUID,
      );
      if (idx >= 0) {
        transportationBenefits.splice(idx, 1, action.payload);
        state.currentTransportationBenefits = transportationBenefits;
      }
    },
    deleteTransportationBenefit: (
      state: ClientState,
      action: {
        type: string;
        payload: TransportationBenefit;
      },
    ) => {
      const transportationBenefits = [...state.currentTransportationBenefits];
      const idx = transportationBenefits.findIndex(
        (pc) =>
          pc.transportationBenefitModelUUID ===
          action.payload.transportationBenefitModelUUID,
      );
      if (idx >= 0) {
        transportationBenefits.splice(idx, 1);
        state.currentTransportationBenefits = transportationBenefits;
      }
    },
    assignTransportationBenefit: (
      state: ClientState,
      action: {
        type: string;
        payload: { servicePlanUUID: string; transportationBenefitUUID: string };
      },
    ) => {
      const servicePlan = state.servicePlanList!.servicePlans.find(
        (p) => p.servicePlanUUID === action.payload.servicePlanUUID,
      );
      if (servicePlan) {
        servicePlan.transportationBenefitUUIDs?.push(
          action.payload.transportationBenefitUUID,
        );
      }
    },
    unassignTransportationBenefit: (
      state: ClientState,
      action: {
        type: string;
        payload: { servicePlanUUID: string; transportationBenefitUUID: string };
      },
    ) => {
      const servicePlan = state.servicePlanList!.servicePlans.find(
        (p) => p.servicePlanUUID === action.payload.servicePlanUUID,
      );
      const idx = servicePlan?.transportationBenefitUUIDs?.findIndex(
        (tpUUID) => tpUUID === action.payload.transportationBenefitUUID,
      );
      if (idx !== undefined && idx >= 0) {
        servicePlan?.transportationBenefitUUIDs?.splice(idx, 1);
      }
    },
    setSavingTransportationBenefit: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingTransportationBenefit = action.payload;
    },
    setSavedTransportationBenefit: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedTransportationBenefit = action.payload;
    },

    // Service plans
    setServicePlanList: (
      state: ClientState,
      action: { type: string; payload: ServicePlanList },
    ) => {
      state.servicePlanList = action.payload;
    },
    setManualServicePlanList: (
      state: ClientState,
      action: { type: string; payload: ServicePlanList | null },
    ) => {
      state.manualServicePlanList = action.payload;
    },
    updateServicePlanList: (
      state: ClientState,
      action: { type: string; payload: ServicePlanList },
    ) => {
      const newList: ServicePlanList = {
        continuationToken: action.payload.continuationToken,
        servicePlans: [
          ...(state.servicePlanList?.servicePlans || []),
          ...action.payload.servicePlans,
        ],
      };
      state.servicePlanList = newList;
    },
    setLoadingServicePlans: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingServicePlans = action.payload;
    },
    setLoadedServicePlans: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedServicePlans = action.payload;
    },
    addServicePlan: (
      state: ClientState,
      action: {
        type: string;
        payload: ServicePlan;
      },
    ) => {
      const planList = state.servicePlanList || { servicePlans: [] };
      planList.servicePlans.push(action.payload);
      state.servicePlanList = planList;
    },
    updateServicePlan: (
      state: ClientState,
      action: {
        type: string;
        payload: ServicePlan;
      },
    ) => {
      const idx = state.servicePlanList!.servicePlans.findIndex(
        (sp) => sp.servicePlanUUID === action.payload.servicePlanUUID,
      );
      if (idx >= 0) {
        state.servicePlanList!.servicePlans.splice(idx, 1, action.payload);
      }
    },
    deleteServicePlan: (
      state: ClientState,
      action: {
        type: string;
        payload: ServicePlan;
      },
    ) => {
      const idx = state.servicePlanList!.servicePlans.findIndex(
        (sp) => sp.servicePlanUUID === action.payload.servicePlanUUID,
      );
      if (idx >= 0) {
        state.servicePlanList?.servicePlans.splice(idx, 1);
      }
    },
    assignServicePlan: (
      state: ClientState,
      action: {
        type: string;
        payload: { programUUID: string; servicePlanUUID: string };
      },
    ) => {
      const program = state.programs.find(
        (p) => p.programUUID === action.payload.programUUID,
      );
      if (program) {
        program.servicePlans?.push(action.payload.servicePlanUUID);
      }
    },
    unassignServicePlan: (
      state: ClientState,
      action: {
        type: string;
        payload: { programUUID: string; servicePlanUUID: string };
      },
    ) => {
      const program = state.programs.find(
        (p) => p.programUUID === action.payload.programUUID,
      );
      const idx = program?.servicePlans?.findIndex(
        (spUUID) => spUUID === action.payload.servicePlanUUID,
      );
      if (idx !== undefined && idx >= 0) {
        program?.servicePlans?.splice(idx, 1);
      }
    },
    setSavingServicePlan: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingServicePlan = action.payload;
    },
    setSavedServicePlan: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedServicePlan = action.payload;
    },

    // Programs
    setPrograms: (
      state: ClientState,
      action: { type: string; payload: Program[] },
    ) => {
      state.programs = action.payload;
    },
    setLoadingPrograms: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingPrograms = action.payload;
    },
    setLoadedPrograms: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedPrograms = action.payload;
    },
    addProgram: (
      state: ClientState,
      action: {
        type: string;
        payload: Program;
      },
    ) => {
      if (action.payload.clientBC === state.currentClient?.clientBC) {
        const programs = state.programs || [];
        programs.push(action.payload);
        state.programs = programs;
      }
    },
    updateProgram: (
      state: ClientState,
      action: {
        type: string;
        payload: Program;
      },
    ) => {
      if (action.payload.clientBC === state.currentClient?.clientBC) {
        const programs = state.programs || [];
        const idx = programs.findIndex(
          (prog) => prog.programUUID === action.payload.programUUID,
        );
        if (idx >= 0) {
          programs.splice(idx, 1, action.payload);
          state.programs = programs;
        }
      }
    },
    deleteProgram: (
      state: ClientState,
      action: {
        type: string;
        payload: Program;
      },
    ) => {
      if (action.payload.clientBC === state.currentClient?.clientBC) {
        const programs = state.programs || [];
        const idx = programs.findIndex(
          (addr) => addr.programUUID === action.payload.programUUID,
        );
        if (idx >= 0) {
          programs.splice(idx, 1);
          state.programs = programs;
        }
      }
    },
    setSavingProgram: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingProgram = action.payload;
    },
    setSavedProgram: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedProgram = action.payload;
    },

    // Passenger Client Data
    setPassengerClientData: (
      state: ClientState,
      action: { type: string; payload: PassengerClientData },
    ) => {
      state.passengerClientData = action.payload;
    },
    setLoadingPassengerClientData: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingPassengerClientData = action.payload;
    },
    setLoadedPassengerClientData: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedPassengerClientData = action.payload;
    },

    // Transportation Benefit Models
    setTransportationBenefitModels: (
      state: ClientState,
      action: { type: string; payload: TransportationBenefitModel[] },
    ) => {
      state.allTransportationBenefitModels = action.payload;
      state.activeTransportationBenefitModels = action.payload.filter(
        (pm) => pm.state === 'active',
      );
    },
    setLoadingTransportationBenefitModels: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingTransportationBenefitModels = action.payload;
    },
    setLoadedTransportationBenefitModels: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedTransportationBenefitModels = action.payload;
    },
    createTransportationBenefitModel: (
      state: ClientState,
      action: { type: string; payload: TransportationBenefitModel },
    ) => {
      state.allTransportationBenefitModels.unshift(action.payload);
    },
    updateTransportationBenefitModel: (
      state: ClientState,
      action: { type: string; payload: TransportationBenefitModel },
    ) => {
      const idx = state.allTransportationBenefitModels.findIndex(
        (tbm) =>
          tbm.transportationBenefitModelUUID ===
          action.payload.transportationBenefitModelUUID,
      );
      if (idx >= 0) {
        state.allTransportationBenefitModels.splice(idx, 1, action.payload);
      }
    },
    deleteTransportationBenefitModel: (
      state: ClientState,
      action: { type: string; payload: TransportationBenefitModel },
    ) => {
      const idx = state.allTransportationBenefitModels.findIndex(
        (tbm) =>
          tbm.transportationBenefitModelUUID ===
          action.payload.transportationBenefitModelUUID,
      );
      if (idx >= 0) {
        state.allTransportationBenefitModels.splice(idx, 1);
      }
    },
    setSavingTransportationBenefitModel: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingTransportationBenefitModel = action.payload;
    },
    setSavedTransportationBenefitModel: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedTransportationBenefitModel = action.payload;
    },

    // Transportation benefit functions
    setTransportationBenefitFunctions: (
      state: ClientState,
      action: { type: string; payload: TransportationBenefitFunction[] },
    ) => {
      state.transportationBenefitFunctions = action.payload;
    },
    setLoadingTransportationBenefitFunctions: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingTransportationBenefitFunctions = action.payload;
    },
    setLoadedTransportationBenefitFunctions: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedTransportationBenefitFunctions = action.payload;
    },

    // Client Profile Program Settings
    setProgramSettingsById: (
      state: ClientState,
      action: { type: string; payload: any },
    ) => {
      state.programSettingsById = action.payload;
    },
    setProgramSettings: (
      state: ClientState,
      action: { type: string; payload: any[] },
    ) => {
      action.payload.forEach((ps) => {
        state.programSettingsById[ps.programSettingUUID] = ps;
      });
    },
    setLoadingProgramSettings: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingProgramSettings = action.payload;
    },
    setLoadedProgramSettings: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedProgramSettings = action.payload;
    },
    setSavingProgramSettings: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingProgramSetting = action.payload;
    },
    setSavedProgramSettings: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedProgramSetting = action.payload;
    },
    createProgramSetting: (
      state: ClientState,
      action: { type: string; payload: any },
    ) => {
      state.programSettingsById[action.payload.programSettingUUID] =
        action.payload;
    },
    updateProgramSetting: (
      state: ClientState,
      action: { type: string; payload: any },
    ) => {
      state.programSettingsById[action.payload.programSettingUUID] =
        action.payload;
    },
    deleteProgramSetting: (
      state: ClientState,
      action: { type: string; payload: any },
    ) => {
      delete state.programSettingsById[action.payload.programSettingUUID];
    },

    // Named Locations
    setNamedLocations: (
      state: ClientState,
      action: { type: string; payload: NamedLocation[] },
    ) => {
      state.allNamedLocations = action.payload;
    },
    setLoadingNamedLocations: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingNamedLocations = action.payload;
    },
    setLoadedNamedLocations: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedNamedLocations = action.payload;
    },
    setSavingNamedLocation: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingNamedLocation = action.payload;
    },
    setSavedNamedLocation: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedNamedLocation = action.payload;
    },
    createNamedLocation: (
      state: ClientState,
      action: { type: string; payload: NamedLocation },
    ) => {
      state.allNamedLocations.push(action.payload);
    },
    updateNamedLocation: (
      state: ClientState,
      action: { type: string; payload: NamedLocation },
    ) => {
      const idx = state.allNamedLocations.findIndex(
        (nl) => nl.namedLocationUUID === action.payload.namedLocationUUID,
      );
      if (idx >= 0) {
        state.allNamedLocations[idx] = action.payload;
      }
    },
    deleteNamedLocation: (
      state: ClientState,
      action: { type: string; payload: string },
    ) => {
      const idx = state.allNamedLocations.findIndex(
        (nl) => nl.namedLocationUUID === action.payload,
      );
      if (idx >= 0) {
        state.allNamedLocations.splice(idx, 1);
      }
    },
    reset: (state: ClientState) => {
      state.savingNamedLocation = false;
      state.savedNamedLocation = false;
      state.savingClientLocation = false;
      state.savedClientLocation = false;
    },

    // Client Locations
    setClientLocations: (
      state: ClientState,
      action: { type: string; payload: ClientLocation[] },
    ) => {
      state.allClientLocations = action.payload;
    },
    setLoadingClientLocations: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingClientLocations = action.payload;
    },
    setLoadedClientLocations: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedClientLocations = action.payload;
    },
    setSavingClientLocation: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingClientLocation = action.payload;
    },
    setSavedClientLocation: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedClientLocation = action.payload;
    },
    createClientLocation: (
      state: ClientState,
      action: { type: string; payload: ClientLocation },
    ) => {
      state.allClientLocations.push(action.payload);
    },
    deleteClientLocation: (
      state: ClientState,
      action: { type: string; payload: string },
    ) => {
      const idx = state.allClientLocations.findIndex(
        (cl) => cl.clientLocationUUID === action.payload,
      );
      if (idx >= 0) {
        state.allClientLocations.splice(idx, 1);
      }
    },

    // Client Preferences
    setClientSettings: (
      state: ClientState,
      action: {
        type: string;
        payload: ClientSetting[];
      },
    ) => {
      state.clientSettings = action.payload;
    },
    setLoadingClientSettings: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingClientSettings = action.payload;
    },
    setLoadedClientSettings: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedClientSettings = action.payload;
    },
    setSavingClientSettings: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingClientSettings = action.payload;
    },
    setSavedClientSettings: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedClientSettings = action.payload;
    },

    // Client Models
    setSavingClientModels: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingClientModels = action.payload;
    },
    setSavedClientModels: (
      state: ClientState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedClientModels = action.payload;
    },

    //Client Admin Settings
    setClientAdminSettings: (
      state: ClientState,
      action: { type: string; payload: ClientAdminSettings },
    ) => {
      state.clientAdminSettings = action.payload;
    },
  },
});

// Clients List
export const { setClientsList } = slice.actions;
export const { setLoadingClientsList } = slice.actions;
export const { setLoadedClientsList } = slice.actions;

// Clients
export const { setClients } = slice.actions;
export const { setLoadingClients } = slice.actions;
export const { setLoadedClients } = slice.actions;
export const { setCurrentClient } = slice.actions;
export const { resetCurrentClientInfo } = slice.actions;
export const { setLoadingCurrentClient } = slice.actions;
export const { setLoadedCurrentClient } = slice.actions;
export const { addClient } = slice.actions;
export const { updateClient } = slice.actions;
export const { deleteClient } = slice.actions;
export const { setSavingClient } = slice.actions;
export const { setSavedClient } = slice.actions;

// Addresses
export const { setAddresses } = slice.actions;
export const { setLoadingAddresses } = slice.actions;
export const { setLoadedAddresses } = slice.actions;
export const { addAddress } = slice.actions;
export const { updateAddress } = slice.actions;
export const { deleteAddress } = slice.actions;
export const { setSavingAddress } = slice.actions;
export const { setSavedAddress } = slice.actions;

// Contacts
export const { setContacts } = slice.actions;
export const { setLoadingContacts } = slice.actions;
export const { setLoadedContacts } = slice.actions;
export const { addContact } = slice.actions;
export const { updateContact } = slice.actions;
export const { deleteContact } = slice.actions;
export const { setSavingContact } = slice.actions;
export const { setSavedContact } = slice.actions;

// Member contact
export const { setMemberContact } = slice.actions;
export const { setLoadingMemberContact } = slice.actions;

// Admins
export const { setAdmins } = slice.actions;
export const { setClientAdmins } = slice.actions;
export const { addClientAdmin } = slice.actions;
export const { deleteClientAdmin } = slice.actions;
export const { setLoadingAdmins } = slice.actions;
export const { setLoadedAdmins } = slice.actions;
export const { setSavingAdmin } = slice.actions;
export const { setSavedAdmin } = slice.actions;
export const { setAdminError } = slice.actions;

// Purpose codes
export const { setPurposeCodes } = slice.actions;
export const { setLoadingPurposeCodes } = slice.actions;
export const { setLoadedPurposeCodes } = slice.actions;
export const { addPurposeCode } = slice.actions;
export const { updatePurposeCode } = slice.actions;
export const { deletePurposeCode } = slice.actions;
export const { assignPurposeCode } = slice.actions;
export const { unassignPurposeCode } = slice.actions;
export const { setSavingPurposeCode } = slice.actions;
export const { setSavedPurposeCode } = slice.actions;

// Service areas
export const { setServiceAreas } = slice.actions;
export const { setTransportationBenefits } = slice.actions;
export const { setLoadingServiceAreas } = slice.actions;
export const { setLoadedServiceAreas } = slice.actions;
export const { addServiceArea } = slice.actions;
export const { updateServiceArea } = slice.actions;
export const { deleteServiceArea } = slice.actions;
export const { assignServiceArea } = slice.actions;
export const { unassignServiceArea } = slice.actions;
export const { setSavingServiceArea } = slice.actions;
export const { setSavedServiceArea } = slice.actions;

// Transportation benefits
export const { setLoadingTransportationBenefits } = slice.actions;
export const { setLoadedTransportationBenefits } = slice.actions;
export const { addTransportationBenefit } = slice.actions;
export const { createTransportationBenefit } = slice.actions;
export const { updateTransportationBenefit } = slice.actions;
export const { deleteTransportationBenefit } = slice.actions;
export const { assignTransportationBenefit } = slice.actions;
export const { unassignTransportationBenefit } = slice.actions;
export const { setSavingTransportationBenefit } = slice.actions;
export const { setSavedTransportationBenefit } = slice.actions;

// Service plans
export const { setServicePlanList } = slice.actions;
export const { setManualServicePlanList } = slice.actions;
export const { updateServicePlanList } = slice.actions;
export const { setLoadingServicePlans } = slice.actions;
export const { setLoadedServicePlans } = slice.actions;
export const { addServicePlan } = slice.actions;
export const { updateServicePlan } = slice.actions;
export const { deleteServicePlan } = slice.actions;
export const { assignServicePlan } = slice.actions;
export const { unassignServicePlan } = slice.actions;
export const { setSavingServicePlan } = slice.actions;
export const { setSavedServicePlan } = slice.actions;

// Programs
export const { setPrograms } = slice.actions;
export const { setLoadingPrograms } = slice.actions;
export const { setLoadedPrograms } = slice.actions;
export const { addProgram } = slice.actions;
export const { updateProgram } = slice.actions;
export const { deleteProgram } = slice.actions;
export const { setSavingProgram } = slice.actions;
export const { setSavedProgram } = slice.actions;

// Passenger Client Data
export const { setPassengerClientData } = slice.actions;
export const { setLoadingPassengerClientData } = slice.actions;
export const { setLoadedPassengerClientData } = slice.actions;

// Transportation benefit models
export const { setTransportationBenefitModels } = slice.actions;
export const { setLoadingTransportationBenefitModels } = slice.actions;
export const { setLoadedTransportationBenefitModels } = slice.actions;
export const { createTransportationBenefitModel } = slice.actions;
export const { updateTransportationBenefitModel } = slice.actions;
export const { deleteTransportationBenefitModel } = slice.actions;
export const { setSavingTransportationBenefitModel } = slice.actions;
export const { setSavedTransportationBenefitModel } = slice.actions;

// Transportation benefit functions
export const { setTransportationBenefitFunctions } = slice.actions;
export const { setLoadingTransportationBenefitFunctions } = slice.actions;
export const { setLoadedTransportationBenefitFunctions } = slice.actions;

// Client Profile Program Settings
export const { setProgramSettingsById } = slice.actions;
export const { setLoadingProgramSettings } = slice.actions;
export const { setLoadedProgramSettings } = slice.actions;
export const { setSavingProgramSettings } = slice.actions;
export const { setSavedProgramSettings } = slice.actions;
export const { createProgramSetting } = slice.actions;
export const { updateProgramSetting } = slice.actions;
export const { deleteProgramSetting } = slice.actions;

// Named Locations
export const { setNamedLocations } = slice.actions;
export const { setLoadingNamedLocations } = slice.actions;
export const { setLoadedNamedLocations } = slice.actions;
export const { setSavingNamedLocation } = slice.actions;
export const { setSavedNamedLocation } = slice.actions;
export const { createNamedLocation } = slice.actions;
export const { updateNamedLocation } = slice.actions;
export const { deleteNamedLocation } = slice.actions;
export const { reset } = slice.actions;

// Client Locations
export const { setClientLocations } = slice.actions;
export const { setLoadingClientLocations } = slice.actions;
export const { setLoadedClientLocations } = slice.actions;
export const { setSavingClientLocation } = slice.actions;
export const { setSavedClientLocation } = slice.actions;
export const { createClientLocation } = slice.actions;
export const { deleteClientLocation } = slice.actions;

// Client Preferences
export const { setClientSettings } = slice.actions;
export const { setLoadingClientSettings } = slice.actions;
export const { setLoadedClientSettings } = slice.actions;
export const { setSavingClientSettings } = slice.actions;
export const { setSavedClientSettings } = slice.actions;

// Client models
export const { setSavingClientModels } = slice.actions;
export const { setSavedClientModels } = slice.actions;

//Client Admin Settings
export const { setClientAdminSettings } = slice.actions;

// Clients
export const fetchClientsListAsync =
  (search?: string, page?: number, pageSize?: number) =>
  (dispatch: Function) => {
    dispatch(setLoadingClientsList(true));
    dispatch(setLoadedClientsList(false));
    clientService.getClientsList(search, page, pageSize).subscribe({
      next: (clientsList) => {
        dispatch(setClientsList(clientsList));
        dispatch(setLoadingClientsList(false));
        dispatch(setLoadedClientsList(true));
      },
      error: (err) => {
        dispatch(setLoadingClientsList(false));
        dispatch(setLoadedClientsList(false));
      },
    });
  };

export const fetchClientsAsync = () => (dispatch: Function) => {
  dispatch(setLoadingClients(true));
  dispatch(setLoadedClients(false));
  clientService.getClients().subscribe({
    next: (clients) => {
      dispatch(setClients(clients));
      dispatch(setLoadingClients(false));
      dispatch(setLoadedClients(true));
    },
    error: (err) => {
      dispatch(setLoadingClients(false));
      dispatch(setLoadedClients(false));
    },
  });
};

export const fetchClientAsync = (id: string) => (dispatch: Function) => {
  dispatch(setLoadingCurrentClient(true));
  dispatch(setLoadedCurrentClient(false));
  dispatch(setCurrentClient(null));
  clientService.getClient(id).subscribe({
    next: (client) => {
      dispatch(setCurrentClient(client));
      dispatch(setLoadingCurrentClient(false));
      dispatch(setLoadedCurrentClient(true));
      dispatch(fetchClientProgramsAsync(id));
    },
    error: (err) => {
      dispatch(setLoadingCurrentClient(false));
      dispatch(setLoadedCurrentClient(false));
    },
  });
};

export const assignClientToParentOrganizationAsync =
  (clientBC: string, parentOrganizationBC: string) => (dispatch: Function) => {
    clientService
      .assignClientToParentOrganization(clientBC, parentOrganizationBC)
      .subscribe({
        next: (client) => {
          dispatch(
            uiSlice.setSuccessMessage(
              'snackbar.assignClientToParentOrganizationSuccess',
            ),
          );
          dispatch(setCurrentClient(client));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
        },
      });
  };

export const createClientAsync =
  (client: Client, effectiveStartDate?: string, effectiveEndDate?: string) =>
  (dispatch: Function) => {
    dispatch(setSavingClient(true));
    dispatch(setSavedClient(false));
    dispatch(setCurrentClient(null));
    clientService.createClient(client).subscribe({
      next: (client) => {
        dispatch(
          updateClientEffectiveDatesAsync(
            client.clientBC,
            effectiveStartDate || 'clear',
            effectiveEndDate || 'clear',
            false,
          ),
        );
        dispatch(uiSlice.setSuccessMessage('snackbar.createClientSuccess'));
        dispatch(addClient(client));
        dispatch(setCurrentClient(client));
        dispatch(setSavingClient(false));
        dispatch(setSavedClient(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingClient(false));
        dispatch(setSavedClient(false));
      },
    });
  };

export const updateClientAsync =
  (client: Client, effectiveStartDate?: string, effectiveEndDate?: string) =>
  (dispatch: Function) => {
    dispatch(setSavingClient(true));
    dispatch(setSavedClient(false));
    dispatch(setCurrentClient(null));
    clientService.updateClient(client).subscribe({
      next: (client) => {
        dispatch(
          updateClientEffectiveDatesAsync(
            client.clientBC,
            effectiveStartDate || 'clear',
            effectiveEndDate || 'clear',
            false,
          ),
        );
        dispatch(uiSlice.setSuccessMessage('snackbar.updateClientSuccess'));
        dispatch(addClient(client));
        dispatch(setCurrentClient(client));
        dispatch(setSavingClient(false));
        dispatch(setSavedClient(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingClient(false));
        dispatch(setSavedClient(false));
      },
    });
  };

export const updateClientEffectiveDatesAsync =
  (
    clientBC: string,
    effectiveStartDate?: string,
    effectiveEndDate?: string,
    showSnackbar: boolean = true,
  ) =>
  (dispatch: Function) => {
    dispatch(setSavingClient(true));
    dispatch(setSavedClient(false));
    clientService
      .updateClientEffectiveDates(
        clientBC,
        effectiveStartDate,
        effectiveEndDate,
      )
      .subscribe({
        next: (updatedClient) => {
          if (showSnackbar) {
            dispatch(
              uiSlice.setSuccessMessage('snackbar.activateClientSuccess'),
            );
          }
          dispatch(updateClient(updatedClient));
          dispatch(setSavingClient(false));
          dispatch(setSavedClient(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingClient(false));
          dispatch(setSavedClient(false));
        },
      });
  };

// Addresses
export const fetchClientAddressesAsync =
  (clientBC: string, states?: string[]) => (dispatch: Function) => {
    dispatch(setLoadingAddresses(true));
    dispatch(setLoadedAddresses(false));
    dispatch(setAddresses([]));
    clientService.getClientAddresses(clientBC, states).subscribe({
      next: (addresses) => {
        dispatch(setAddresses(addresses));
        dispatch(setLoadingAddresses(false));
        dispatch(setLoadedAddresses(true));
      },
      error: (err) => {
        dispatch(setLoadingAddresses(false));
        dispatch(setLoadedAddresses(false));
      },
    });
  };

export const createClientAddressAsync =
  (clientBC: string, address: ClientAddress) => (dispatch: Function) => {
    dispatch(setSavingAddress(true));
    dispatch(setSavedAddress(false));
    clientService.createClientAddress(clientBC, address).subscribe({
      next: (newAddress) => {
        dispatch(uiSlice.setSuccessMessage('snackbar.createAddressSuccess'));
        dispatch(addAddress({ clientBC, address: newAddress }));
        dispatch(setSavingAddress(false));
        dispatch(setSavedAddress(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingClient(false));
        dispatch(setSavedClient(false));
      },
    });
  };

export const updateClientAddressAsync =
  (clientBC: string, address: ClientAddress) => (dispatch: Function) => {
    dispatch(setSavingAddress(true));
    dispatch(setSavedAddress(false));
    clientService.updateClientAddress(clientBC, address).subscribe({
      next: (newAddress) => {
        dispatch(uiSlice.setSuccessMessage('snackbar.updateAddressSuccess'));
        dispatch(updateAddress({ clientBC, address: newAddress }));
        dispatch(setSavingAddress(false));
        dispatch(setSavedAddress(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingClient(false));
        dispatch(setSavedClient(false));
      },
    });
  };

export const activateClientAddressAsync =
  (clientBC: string, address: ClientAddress) => (dispatch: Function) => {
    dispatch(setSavingAddress(true));
    dispatch(setSavedAddress(false));
    clientService.activateClientAddress(clientBC, address!).subscribe({
      next: (updatedAddress) => {
        dispatch(uiSlice.setSuccessMessage('snackbar.activateAddressSuccess'));
        dispatch(updateAddress({ clientBC, address: updatedAddress }));
        dispatch(setSavingAddress(false));
        dispatch(setSavedAddress(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingAddress(false));
        dispatch(setSavedAddress(false));
      },
    });
  };

export const deactivateClientAddressAsync =
  (clientBC: string, address: ClientAddress) => (dispatch: Function) => {
    dispatch(setSavingAddress(true));
    dispatch(setSavedAddress(false));
    clientService.deactivateClientAddress(clientBC, address!).subscribe({
      next: (updatedAddress) => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.deactivateAddressSuccess'),
        );
        dispatch(updateAddress({ clientBC, address: updatedAddress }));
        dispatch(setSavingAddress(false));
        dispatch(setSavedAddress(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingAddress(false));
        dispatch(setSavedAddress(false));
      },
    });
  };

export const deleteClientAsync = (clientBC: string) => (dispatch: Function) => {
  dispatch(setSavingClient(true));
  dispatch(setSavedClient(false));
  clientService.deleteClient(clientBC).subscribe({
    next: () => {
      dispatch(uiSlice.setSuccessMessage('snackbar.deleteClientSuccess'));
      dispatch(deleteClient(clientBC));
      dispatch(setSavingClient(false));
      dispatch(setSavedClient(true));
    },
    error: (err) => {
      dispatch(uiSlice.setErrorMessage(err.message));
      dispatch(setSavingClient(false));
      dispatch(setSavedClient(false));
    },
  });
};

export const deleteClientAddressAsync =
  (clientBC: string, address: ClientAddress) => (dispatch: Function) => {
    dispatch(setSavingAddress(true));
    dispatch(setSavedAddress(false));
    clientService
      .deleteClientAddress(clientBC, address.clientAddressUUID!)
      .subscribe({
        next: () => {
          dispatch(uiSlice.setSuccessMessage('snackbar.deleteAddressSuccess'));
          dispatch(
            deleteAddress({
              clientBC,
              addressUUID: address.clientAddressUUID!,
            }),
          );
          dispatch(setSavingAddress(false));
          dispatch(setSavedAddress(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingClient(false));
          dispatch(setSavedClient(false));
        },
      });
  };

// Contacts
export const createClientContactAsync =
  (clientBC: string, contact: ClientContact) => (dispatch: Function) => {
    dispatch(setSavingContact(true));
    dispatch(setSavedContact(false));
    clientService.createClientContact(clientBC, contact).subscribe({
      next: (newContact) => {
        dispatch(uiSlice.setSuccessMessage('snackbar.createContactSuccess'));
        dispatch(addContact({ clientBC, contact: newContact }));
        dispatch(setSavingContact(false));
        dispatch(setSavedContact(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingContact(false));
        dispatch(setSavedContact(false));
      },
    });
  };

// Member contact
export const fetchClientMemberContactAsync =
  (clientBC: string, servicePlanID: string) =>
  (dispatch: Function, getState: () => { client: ClientState }) => {
    // Dedupe against the live store: useClientMemberContact mounts several
    // times per dialog (applier + each address field), and their effects all
    // run in the same commit with pre-dispatch snapshots, so the hook-side
    // guard alone can't see an in-flight fetch.
    const { memberContactByClientPlan, loadingMemberContactByClientPlan } =
      getState().client;
    const key = memberContactKey(clientBC, servicePlanID);
    // undefined = never requested; null = settled miss (don't refetch).
    if (memberContactByClientPlan[key] !== undefined) return;
    if (loadingMemberContactByClientPlan[key]) return;
    dispatch(
      setLoadingMemberContact({ clientBC, servicePlanID, loading: true }),
    );
    clientService.getClientMemberContact(clientBC, servicePlanID).subscribe({
      next: (contact) => {
        dispatch(
          setMemberContact({
            clientBC,
            servicePlanID,
            contact: contact ?? null,
          }),
        );
        dispatch(
          setLoadingMemberContact({ clientBC, servicePlanID, loading: false }),
        );
      },
      error: (err) => {
        // Record the miss (null) so consumers render the generic fallback
        // support strings instead of refetching on every mount.
        dispatch(setMemberContact({ clientBC, servicePlanID, contact: null }));
        dispatch(
          setLoadingMemberContact({ clientBC, servicePlanID, loading: false }),
        );
      },
    });
  };

// Contacts
export const fetchClientContactsAsync =
  (clientBC: string, states?: string[]) => (dispatch: Function) => {
    dispatch(setLoadingContacts(true));
    dispatch(setLoadedContacts(false));
    dispatch(setContacts([]));
    clientService.getClientContacts(clientBC, states).subscribe({
      next: (contacts) => {
        dispatch(setContacts(contacts));
        dispatch(setLoadingContacts(false));
        dispatch(setLoadedContacts(true));
      },
      error: (err) => {
        dispatch(setLoadingContacts(false));
        dispatch(setLoadedContacts(false));
      },
    });
  };

export const updateClientContactAsync =
  (clientBC: string, contact: ClientContact) => (dispatch: Function) => {
    dispatch(setSavingContact(true));
    dispatch(setSavedContact(false));
    clientService.updateClientContact(clientBC, contact).subscribe({
      next: (newContact) => {
        dispatch(uiSlice.setSuccessMessage('snackbar.updateContactSuccess'));
        dispatch(updateContact({ clientBC, contact: newContact }));
        dispatch(setSavingContact(false));
        dispatch(setSavedContact(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingContact(false));
        dispatch(setSavedContact(false));
      },
    });
  };

export const activateClientContactAsync =
  (clientBC: string, contact: ClientContact) => (dispatch: Function) => {
    dispatch(setSavingContact(true));
    dispatch(setSavedContact(false));
    clientService.activateClientContact(clientBC, contact!).subscribe({
      next: (updatedContact) => {
        dispatch(uiSlice.setSuccessMessage('snackbar.activateContactSuccess'));
        dispatch(updateContact({ clientBC, contact: updatedContact }));
        dispatch(setSavingContact(false));
        dispatch(setSavedContact(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingContact(false));
        dispatch(setSavedContact(false));
      },
    });
  };

export const deactivateClientContactAsync =
  (clientBC: string, contact: ClientContact) => (dispatch: Function) => {
    dispatch(setSavingContact(true));
    dispatch(setSavedContact(false));
    clientService.deactivateClientContact(clientBC, contact!).subscribe({
      next: (updatedContact) => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.deactivateContactSuccess'),
        );
        dispatch(updateContact({ clientBC, contact: updatedContact }));
        dispatch(setSavingContact(false));
        dispatch(setSavedContact(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingContact(false));
        dispatch(setSavedContact(false));
      },
    });
  };

export const deleteClientContactAsync =
  (clientBC: string, contact: ClientContact) => (dispatch: Function) => {
    dispatch(setSavingContact(true));
    dispatch(setSavedContact(false));
    clientService
      .deleteClientContact(clientBC, contact.clientContactUUID!)
      .subscribe({
        next: () => {
          dispatch(uiSlice.setSuccessMessage('snackbar.deleteContactSuccess'));
          dispatch(
            deleteContact({
              clientBC,
              contactUUID: contact.clientContactUUID!,
            }),
          );
          dispatch(setSavingContact(false));
          dispatch(setSavedContact(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingContact(false));
          dispatch(setSavedContact(false));
        },
      });
  };

// Admins
export const fetchClientAdminsAsync =
  (clientBC: string, page: number, pageSize: number, userRoleIDs: number[]) =>
  (dispatch: Function) => {
    dispatch(
      setClientAdmins({
        clientBC,
        admins: {
          itemCount: 0,
          currentPage: 0,
          totalPages: 0,
          items: [],
        },
      }),
    );
    dispatch(setLoadingAdmins(true));
    dispatch(setLoadedAdmins(false));
    dispatch(setAdminError(null));
    userService
      .getClientAdmins(clientBC, page, pageSize, userRoleIDs)
      .subscribe({
        next: (admins: PagedList<ClientAdmin>) => {
          dispatch(setClientAdmins({ clientBC, admins }));
          dispatch(setLoadingAdmins(false));
          dispatch(setLoadedAdmins(true));
        },
        error: (err) => {
          dispatch(setAdminError(err?.message));
          dispatch(setLoadingAdmins(false));
          dispatch(setLoadedAdmins(false));
        },
      });
  };

export const createClientAdminAsync =
  (
    clientBC: string,
    clientAdminUserData: Partial<ClientAdmin>,
    userRoleIDs: number[],
  ) =>
  (dispatch: Function) => {
    dispatch(setSavingAdmin(true));
    dispatch(setSavedAdmin(false));
    dispatch(setAdminError(null));
    userService
      .createClientAdmin(clientBC, clientAdminUserData, userRoleIDs)
      .subscribe({
        next: (data: ClientAdmin) => {
          dispatch(addClientAdmin({ clientBC, admin: data }));
          dispatch(uiSlice.setSuccessMessage('snackbar.addAdminSuccess'));
          dispatch(setSavingAdmin(false));
          dispatch(setSavedAdmin(true));
        },
        error: (err) => {
          dispatch(setAdminError(err.message));
          dispatch(
            uiSlice.setErrorMessage('snackbar.addAdminFailed', err.message),
          );
          dispatch(setSavingAdmin(false));
          dispatch(setSavedAdmin(false));
        },
      });
  };

export const resendClientAdminInviteAsync =
  (clientBC: string, userAccountID: number) => (dispatch: Function) => {
    dispatch(setSavingAdmin(true));
    dispatch(setSavedAdmin(false));
    dispatch(setAdminError(null));
    userService.resendClientAdminInvite(userAccountID, clientBC).subscribe({
      next: () => {
        dispatch(uiSlice.setSuccessMessage('snackbar.resendInviteSuccess'));
        dispatch(setSavingAdmin(false));
        dispatch(setSavedAdmin(true));
      },
      error: (err) => {
        dispatch(setAdminError(err.message));
        dispatch(uiSlice.setErrorMessage('snackbar.resendAdminInviteFailed'));
        dispatch(setSavingAdmin(false));
        dispatch(setSavedAdmin(false));
      },
    });
  };

export const deleteClientAdminAsync =
  (clientBC: string, userAccountID: number) => (dispatch: Function) => {
    dispatch(setSavingAdmin(true));
    dispatch(setSavedAdmin(false));
    dispatch(setAdminError(null));
    userService.deleteClientAdmin(userAccountID).subscribe({
      next: () => {
        dispatch(deleteClientAdmin({ clientBC, userAccountID }));
        dispatch(uiSlice.setSuccessMessage('snackbar.deleteAdminSuccess'));
        dispatch(setSavingAdmin(false));
        dispatch(setSavedAdmin(true));
      },
      error: (err) => {
        dispatch(setAdminError(err.message));
        dispatch(uiSlice.setErrorMessage('snackbar.deleteAdminFailed'));
        dispatch(setSavingAdmin(false));
        dispatch(setSavedAdmin(false));
      },
    });
  };

// Purpose codes
export const fetchClientPurposeCodesAsync =
  (clientBC: string, states?: string[]) => (dispatch: Function) => {
    dispatch(setLoadingPurposeCodes(true));
    dispatch(setLoadedPurposeCodes(false));
    clientService.getPurposeCodes(clientBC, states).subscribe({
      next: (purposeCodeInfo) => {
        dispatch(setPurposeCodes(purposeCodeInfo.purposeCodes));
        dispatch(setLoadingPurposeCodes(false));
        dispatch(setLoadedPurposeCodes(true));
      },
      error: (err) => {
        dispatch(setLoadingPurposeCodes(false));
        dispatch(setLoadedPurposeCodes(false));
      },
    });
  };

export const createClientPurposeCodeAsync =
  (purposeCode: ClientPurposeCode) => (dispatch: Function) => {
    dispatch(setSavingPurposeCode(true));
    dispatch(setSavedPurposeCode(false));
    clientService.createClientPurposeCode(purposeCode).subscribe({
      next: (newPurposeCode) => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.createPurposeCodeSuccess'),
        );
        dispatch(addPurposeCode(newPurposeCode));
        dispatch(setSavingPurposeCode(false));
        dispatch(setSavedPurposeCode(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingPurposeCode(false));
        dispatch(setSavedPurposeCode(false));
      },
    });
  };

export const deleteClientPurposeCodeAsync =
  (clientBC: string, purposeCode: ClientPurposeCode) =>
  (dispatch: Function) => {
    dispatch(setSavingPurposeCode(true));
    dispatch(setSavedPurposeCode(false));
    clientService
      .deletePurposeCode(clientBC, purposeCode.purposeCodeUUID!)
      .subscribe({
        next: () => {
          dispatch(
            uiSlice.setSuccessMessage('snackbar.deletePurposeCodeSuccess'),
          );
          dispatch(deletePurposeCode(purposeCode));
          dispatch(setSavingPurposeCode(false));
          dispatch(setSavedPurposeCode(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingPurposeCode(false));
          dispatch(setSavedPurposeCode(false));
        },
      });
  };

export const activateClientPurposeCodeAsync =
  (clientBC: string, purposeCode: ClientPurposeCode) =>
  (dispatch: Function) => {
    dispatch(setSavingPurposeCode(true));
    dispatch(setSavedPurposeCode(false));
    clientService.activatePurposeCode(clientBC, purposeCode!).subscribe({
      next: (purposeCode) => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.activatePurposeCodeSuccess'),
        );
        dispatch(updatePurposeCode(purposeCode));
        dispatch(setSavingPurposeCode(false));
        dispatch(setSavedPurposeCode(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingPurposeCode(false));
        dispatch(setSavedPurposeCode(false));
      },
    });
  };

export const deactivateClientPurposeCodeAsync =
  (clientBC: string, purposeCode: ClientPurposeCode) =>
  (dispatch: Function) => {
    dispatch(setSavingPurposeCode(true));
    dispatch(setSavedPurposeCode(false));
    clientService.deactivatePurposeCode(clientBC, purposeCode!).subscribe({
      next: (purposeCode) => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.deactivatePurposeCodeSuccess'),
        );
        dispatch(updatePurposeCode(purposeCode));
        dispatch(setSavingPurposeCode(false));
        dispatch(setSavedPurposeCode(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingPurposeCode(false));
        dispatch(setSavedPurposeCode(false));
      },
    });
  };

export const assignClientPurposeCodeAsync =
  (clientBC: string, purposeCodeUUID: string, servicePlanUUID: string) =>
  (dispatch: Function) => {
    dispatch(setSavingServicePlan(true));
    dispatch(setSavedServicePlan(false));
    clientService
      .assignClientPurposeCode(clientBC, purposeCodeUUID, servicePlanUUID)
      .subscribe({
        next: () => {
          dispatch(
            uiSlice.setSuccessMessage('snackbar.assignPurposeCodeSuccess'),
          );
          dispatch(assignPurposeCode({ servicePlanUUID, purposeCodeUUID }));
          dispatch(setSavingServicePlan(false));
          dispatch(setSavedServicePlan(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingServicePlan(false));
          dispatch(setSavedServicePlan(false));
        },
      });
  };

export const unassignClientPurposeCodeAsync =
  (clientBC: string, servicePlanUUID: string, purposeCodeUUID: string) =>
  (dispatch: Function) => {
    dispatch(setSavingServicePlan(true));
    dispatch(setSavedServicePlan(false));
    clientService
      .unassignClientPurposeCode(clientBC, purposeCodeUUID, servicePlanUUID)
      .subscribe({
        next: () => {
          dispatch(
            uiSlice.setSuccessMessage('snackbar.unassignPurposeCodeSuccess'),
          );
          dispatch(unassignPurposeCode({ servicePlanUUID, purposeCodeUUID }));
          dispatch(setSavingServicePlan(false));
          dispatch(setSavedServicePlan(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingServicePlan(false));
          dispatch(setSavedServicePlan(false));
        },
      });
  };

// Service areas
export const fetchClientServiceAreasAsync =
  (clientBC: string, states?: string[]) => (dispatch: Function) => {
    dispatch(setLoadingServiceAreas(true));
    dispatch(setLoadedServiceAreas(false));
    clientService.getServiceAreas(clientBC, states).subscribe({
      next: (serviceAreas) => {
        dispatch(setServiceAreas(serviceAreas));
        dispatch(setLoadingServiceAreas(false));
        dispatch(setLoadedServiceAreas(true));
      },
      error: (err) => {
        dispatch(setLoadingServiceAreas(false));
        dispatch(setLoadedServiceAreas(false));
      },
    });
  };

export const addClientServiceAreaAsync =
  (serviceArea: ServicePlanServiceArea) => (dispatch: Function) => {
    dispatch(setSavingServiceArea(true));
    dispatch(setSavedServiceArea(false));
    clientService.createServiceArea(serviceArea).subscribe({
      next: (newServiceArea) => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.createServiceAreaSuccess'),
        );
        dispatch(addServiceArea(newServiceArea));
        dispatch(setSavingServiceArea(false));
        dispatch(setSavedServiceArea(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingServiceArea(false));
        dispatch(setSavedServiceArea(false));
      },
    });
  };

export const updateClientServiceAreaAsync =
  (serviceArea: ServicePlanServiceArea) => (dispatch: Function) => {
    dispatch(setSavingServiceArea(true));
    dispatch(setSavedServiceArea(false));
    clientService.updateServiceArea(serviceArea).subscribe({
      next: (newServiceArea) => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.updateServiceAreaSuccess'),
        );
        dispatch(updateServiceArea(newServiceArea));
        dispatch(setSavingServiceArea(false));
        dispatch(setSavedServiceArea(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingServiceArea(false));
        dispatch(setSavedServiceArea(false));
      },
    });
  };

export const deleteClientServiceAreaAsync =
  (clientBC: string, serviceArea: ServicePlanServiceArea) =>
  (dispatch: Function) => {
    dispatch(setSavingServiceArea(true));
    dispatch(setSavedServiceArea(false));
    clientService
      .deleteServiceArea(clientBC, serviceArea.serviceAreaUUID!)
      .subscribe({
        next: () => {
          dispatch(
            uiSlice.setSuccessMessage('snackbar.deleteServiceAreaSuccess'),
          );
          dispatch(deleteServiceArea(serviceArea));
          dispatch(setSavingServiceArea(false));
          dispatch(setSavedServiceArea(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingServiceArea(false));
          dispatch(setSavedServiceArea(false));
        },
      });
  };

export const activateClientServiceAreaAsync =
  (clientBC: string, serviceArea: ServicePlanServiceArea) =>
  (dispatch: Function) => {
    dispatch(setSavingServiceArea(true));
    dispatch(setSavedServiceArea(false));
    clientService.activateServiceArea(clientBC, serviceArea).subscribe({
      next: (serviceArea) => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.activateServiceAreaSuccess'),
        );
        dispatch(updateServiceArea(serviceArea));
        dispatch(setSavingServiceArea(false));
        dispatch(setSavedServiceArea(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingServiceArea(false));
        dispatch(setSavedServiceArea(false));
      },
    });
  };

export const deactivateClientServiceAreaAsync =
  (clientBC: string, serviceArea: ServicePlanServiceArea) =>
  (dispatch: Function) => {
    dispatch(setSavingServiceArea(true));
    dispatch(setSavedServiceArea(false));
    clientService.deactivateServiceArea(clientBC, serviceArea).subscribe({
      next: (serviceArea) => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.deactivateServiceAreaSuccess'),
        );
        dispatch(updateServiceArea(serviceArea));
        dispatch(setSavingServiceArea(false));
        dispatch(setSavedServiceArea(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingServiceArea(false));
        dispatch(setSavedServiceArea(false));
      },
    });
  };

export const assignClientServiceAreaAsync =
  (clientBC: string, serviceAreaUUID: string, servicePlanUUID: string) =>
  (dispatch: Function) => {
    dispatch(setSavingServicePlan(true));
    dispatch(setSavedServicePlan(false));
    clientService
      .assignClientServiceArea(clientBC, serviceAreaUUID, servicePlanUUID)
      .subscribe({
        next: () => {
          dispatch(
            uiSlice.setSuccessMessage('snackbar.assignServiceAreaSuccess'),
          );
          dispatch(assignServiceArea({ servicePlanUUID, serviceAreaUUID }));
          dispatch(setSavingServicePlan(false));
          dispatch(setSavedServicePlan(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingServicePlan(false));
          dispatch(setSavedServicePlan(false));
        },
      });
  };

export const unassignClientServiceAreaAsync =
  (clientBC: string, servicePlanUUID: string, serviceAreaUUID: string) =>
  (dispatch: Function) => {
    dispatch(setSavingServicePlan(true));
    dispatch(setSavedServicePlan(false));
    clientService
      .unassignClientServiceArea(clientBC, serviceAreaUUID, servicePlanUUID)
      .subscribe({
        next: () => {
          dispatch(
            uiSlice.setSuccessMessage('snackbar.unassignServiceAreaSuccess'),
          );
          dispatch(unassignServiceArea({ servicePlanUUID, serviceAreaUUID }));
          dispatch(setSavingServicePlan(false));
          dispatch(setSavedServicePlan(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingServicePlan(false));
          dispatch(setSavedServicePlan(false));
        },
      });
  };

// Transportation benefits
export const fetchTransportationBenefitsAsync =
  (clientBC: string) => (dispatch: Function) => {
    dispatch(setLoadingTransportationBenefits(true));
    dispatch(setLoadedTransportationBenefits(false));
    clientService.getTransportationBenefits(clientBC).subscribe({
      next: (transportationBenefits) => {
        dispatch(setTransportationBenefits(transportationBenefits));
        dispatch(setLoadingTransportationBenefits(false));
        dispatch(setLoadedTransportationBenefits(true));
      },
      error: (err) => {
        dispatch(setLoadingTransportationBenefits(false));
        dispatch(setLoadedTransportationBenefits(false));
      },
    });
  };

export const createTransportationBenefitAsync =
  (
    transportationBenefit: TransportationBenefit,
    options?: { isFirst?: boolean },
  ) =>
  (dispatch: Function) => {
    dispatch(setSavingTransportationBenefit(true));
    dispatch(setSavedTransportationBenefit(false));
    clientService.createTransportationBenefit(transportationBenefit).subscribe({
      next: (newTransportationBenefit) => {
        dispatch(
          uiSlice.setSuccessMessage(
            options?.isFirst
              ? 'snackbar.taskComplete'
              : 'snackbar.createTransportationBenefitSuccess',
          ),
        );
        dispatch(createTransportationBenefit(newTransportationBenefit));
        dispatch(setSavingTransportationBenefit(false));
        dispatch(setSavedTransportationBenefit(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingTransportationBenefit(false));
        dispatch(setSavedTransportationBenefit(false));
      },
    });
  };

export const updateTransportationBenefitAsync =
  (transportationBenefit: TransportationBenefit) => (dispatch: Function) => {
    dispatch(setSavingTransportationBenefit(true));
    dispatch(setSavedTransportationBenefit(false));
    clientService.updateTransportationBenefit(transportationBenefit).subscribe({
      next: (newTransportationBenefit) => {
        dispatch(
          uiSlice.setSuccessMessage(
            'snackbar.updateTransportationBenefitSuccess',
          ),
        );
        dispatch(updateTransportationBenefit(newTransportationBenefit));
        dispatch(setSavingTransportationBenefit(false));
        dispatch(setSavedTransportationBenefit(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingTransportationBenefit(false));
        dispatch(setSavedTransportationBenefit(false));
      },
    });
  };

export const updateTransportationBenefitEffectiveDatesAsync =
  (
    clientBC: string,
    transportationBenefitUUID: string,
    effectiveStartDate?: string,
    effectiveEndDate?: string,
  ) =>
  (dispatch: Function) => {
    dispatch(setSavingTransportationBenefit(true));
    dispatch(setSavedTransportationBenefit(false));
    clientService
      .updateTransportationBenefitEffectiveDates(
        clientBC,
        transportationBenefitUUID,
        effectiveStartDate,
        effectiveEndDate,
      )
      .subscribe({
        next: (newTransportationBenefit) => {
          dispatch(
            uiSlice.setSuccessMessage(
              'snackbar.activateTransportationBenefitSuccess',
            ),
          );
          dispatch(updateTransportationBenefit(newTransportationBenefit));
          dispatch(setSavingTransportationBenefit(false));
          dispatch(setSavedTransportationBenefit(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingTransportationBenefit(false));
          dispatch(setSavedTransportationBenefit(false));
        },
      });
  };

export const deleteTransportationBenefitAsync =
  (clientBC: string, transportationBenefit: TransportationBenefit) =>
  (dispatch: Function) => {
    dispatch(setSavingTransportationBenefit(true));
    dispatch(setSavedTransportationBenefit(false));
    clientService
      .deleteTransportationBenefit(
        clientBC,
        transportationBenefit.transportationBenefitUUID!,
      )
      .subscribe({
        next: () => {
          dispatch(
            uiSlice.setSuccessMessage(
              'snackbar.deleteTransportationBenefitSuccess',
            ),
          );
          dispatch(deleteTransportationBenefit(transportationBenefit));
          dispatch(setSavingTransportationBenefit(false));
          dispatch(setSavedTransportationBenefit(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingTransportationBenefit(false));
          dispatch(setSavedTransportationBenefit(false));
        },
      });
  };

export const assignTransportationBenefitAsync =
  (
    clientBC: string,
    transportationBenefitUUID: string,
    servicePlanUUID: string,
  ) =>
  (dispatch: Function) => {
    dispatch(setSavingServicePlan(true));
    dispatch(setSavedServicePlan(false));
    clientService
      .assignTransportationBenefit(
        clientBC,
        transportationBenefitUUID,
        servicePlanUUID,
      )
      .subscribe({
        next: () => {
          dispatch(
            uiSlice.setSuccessMessage(
              'snackbar.assignTransportationBenefitSuccess',
            ),
          );
          dispatch(
            assignTransportationBenefit({
              servicePlanUUID,
              transportationBenefitUUID,
            }),
          );
          dispatch(setSavingServicePlan(false));
          dispatch(setSavedServicePlan(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingServicePlan(false));
          dispatch(setSavedServicePlan(false));
        },
      });
  };

export const unassignTransportationBenefitAsync =
  (
    clientBC: string,
    servicePlanUUID: string,
    transportationBenefitUUID: string,
  ) =>
  (dispatch: Function) => {
    dispatch(setSavingServicePlan(true));
    dispatch(setSavedServicePlan(false));
    clientService
      .unassignTransportationBenefit(
        clientBC,
        transportationBenefitUUID,
        servicePlanUUID,
      )
      .subscribe({
        next: () => {
          dispatch(
            uiSlice.setSuccessMessage(
              'snackbar.unassignTransportationBenefitSuccess',
            ),
          );
          dispatch(
            unassignTransportationBenefit({
              servicePlanUUID,
              transportationBenefitUUID,
            }),
          );
          dispatch(setSavingServicePlan(false));
          dispatch(setSavedServicePlan(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingServicePlan(false));
          dispatch(setSavedServicePlan(false));
        },
      });
  };

// Service plans
export const fetchClientServicePlansAsync =
  (clientBC: string, continuationToken?: string, states?: string[]) =>
  (dispatch: Function) => {
    dispatch(setLoadingServicePlans(true));
    dispatch(setLoadedServicePlans(false));
    clientService
      .getServicePlans(clientBC, continuationToken, states)
      .subscribe({
        next: (servicePlanList) => {
          if (continuationToken) {
            dispatch(updateServicePlanList(servicePlanList));
          } else {
            dispatch(setServicePlanList(servicePlanList));
          }
          dispatch(setLoadingServicePlans(false));
          dispatch(setLoadedServicePlans(true));
        },
        error: (err) => {
          dispatch(setLoadingServicePlans(false));
          dispatch(setLoadedServicePlans(false));
        },
      });
  };

export const fetchManualClientServicePlansAsync =
  (clientBC: string, states?: string[]) => (dispatch: Function) => {
    dispatch(setLoadingServicePlans(true));
    dispatch(setLoadedServicePlans(false));
    clientService.getManualServicePlans(clientBC, states).subscribe({
      next: (servicePlanList) => {
        dispatch(setManualServicePlanList(servicePlanList));
        dispatch(setLoadingServicePlans(false));
        dispatch(setLoadedServicePlans(true));
      },
      error: (err) => {
        dispatch(setLoadingServicePlans(false));
        dispatch(setLoadedServicePlans(false));
      },
    });
  };

export const createClientServicePlanAsync =
  (servicePlan: ServicePlan) => (dispatch: Function) => {
    dispatch(setSavingServicePlan(true));
    dispatch(setSavedServicePlan(false));
    clientService.createClientServicePlan(servicePlan).subscribe({
      next: (newServicePlan) => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.createServicePlanSuccess'),
        );
        dispatch(addServicePlan(newServicePlan));
        dispatch(setSavingServicePlan(false));
        dispatch(setSavedServicePlan(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingServicePlan(false));
        dispatch(setSavedServicePlan(false));
      },
    });
  };

export const updateClientServicePlanAsync =
  (program: ServicePlan) => (dispatch: Function) => {
    dispatch(setSavingServicePlan(true));
    dispatch(setSavedServicePlan(false));
    clientService.updateClientServicePlan(program).subscribe({
      next: (newServicePlan) => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.updateServicePlanSuccess'),
        );
        dispatch(updateServicePlan(newServicePlan));
        dispatch(setSavingServicePlan(false));
        dispatch(setSavedServicePlan(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingServicePlan(false));
        dispatch(setSavedServicePlan(false));
      },
    });
  };

export const deleteClientServicePlanAsync =
  (clientBC: string, servicePlan: ServicePlan) => (dispatch: Function) => {
    dispatch(setSavingServicePlan(true));
    dispatch(setSavedServicePlan(false));
    clientService
      .deleteServicePlan(clientBC, servicePlan.servicePlanUUID!)
      .subscribe({
        next: () => {
          dispatch(
            uiSlice.setSuccessMessage('snackbar.deleteServicePlanSuccess'),
          );
          dispatch(deleteServicePlan(servicePlan));
          dispatch(setSavingServicePlan(false));
          dispatch(setSavedServicePlan(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingServicePlan(false));
          dispatch(setSavedServicePlan(false));
        },
      });
  };

export const reassignClientServicePlanAsync =
  (clientBC: string, servicePlanUUID: string, programUUID: string) =>
  (dispatch: Function) => {
    dispatch(setSavingServicePlan(true));
    dispatch(setSavedServicePlan(false));
    clientService
      .reassignClientServicePlan(clientBC, servicePlanUUID, programUUID)
      .subscribe({
        next: () => {
          dispatch(
            uiSlice.setSuccessMessage('snackbar.assignServicePlanSuccess'),
          );
          dispatch(assignServicePlan({ programUUID, servicePlanUUID }));
          dispatch(setSavingServicePlan(false));
          dispatch(setSavedServicePlan(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingServicePlan(false));
          dispatch(setSavedServicePlan(false));
        },
      });
  };

export const unassignClientServicePlanAsync =
  (clientBC: string, servicePlanUUID: string, programUUID: string) =>
  (dispatch: Function) => {
    dispatch(setSavingServicePlan(true));
    dispatch(setSavedServicePlan(false));
    clientService
      .unassignClientServicePlan(clientBC, servicePlanUUID, programUUID)
      .subscribe({
        next: () => {
          dispatch(
            uiSlice.setSuccessMessage('snackbar.unassignServicePlanSuccess'),
          );
          dispatch(unassignServicePlan({ programUUID, servicePlanUUID }));
          dispatch(setSavingServicePlan(false));
          dispatch(setSavedServicePlan(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingServicePlan(false));
          dispatch(setSavedServicePlan(false));
        },
      });
  };

export const updateServicePlanEffectiveDatesAsync =
  (
    clientBC: string,
    servicePlanUUID: string,
    effectiveStartDate?: string,
    effectiveEndDate?: string,
  ) =>
  (dispatch: Function) => {
    dispatch(setSavingServicePlan(true));
    dispatch(setSavedServicePlan(false));
    clientService
      .updateServicePlanEffectiveDates(
        clientBC,
        servicePlanUUID,
        effectiveStartDate,
        effectiveEndDate,
      )
      .subscribe({
        next: (servicePlan) => {
          dispatch(
            uiSlice.setSuccessMessage('snackbar.activateServicePlanSuccess'),
          );
          dispatch(updateServicePlan(servicePlan));
          dispatch(setSavingServicePlan(false));
          dispatch(setSavedServicePlan(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingServicePlan(false));
          dispatch(setSavedServicePlan(false));
        },
      });
  };

// Programs
export const fetchClientProgramsAsync =
  (clientBC: string, states?: string[]) => (dispatch: Function) => {
    dispatch(setLoadingPrograms(true));
    dispatch(setLoadedPrograms(false));
    clientService.getPrograms(clientBC, states).subscribe({
      next: (programInfo) => {
        dispatch(setPrograms(programInfo.programs));
        dispatch(setLoadingPrograms(false));
        dispatch(setLoadedPrograms(true));
      },
      error: (err) => {
        dispatch(setLoadingPrograms(false));
        dispatch(setLoadedPrograms(false));
      },
    });
  };

export const createClientProgramAsync =
  (program: Program, options?: { isFirst?: boolean }) =>
  (dispatch: Function) => {
    dispatch(setSavingProgram(true));
    dispatch(setSavedProgram(false));
    clientService.createClientProgram(program).subscribe({
      next: (newProgram) => {
        dispatch(
          uiSlice.setSuccessMessage(
            options?.isFirst
              ? 'snackbar.taskComplete'
              : 'snackbar.createProgramSuccess',
          ),
        );
        dispatch(addProgram(newProgram));
        dispatch(setSavingProgram(false));
        dispatch(setSavedProgram(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingProgram(false));
        dispatch(setSavedProgram(false));
      },
    });
  };

export const updateClientProgramAsync =
  (program: Program) => (dispatch: Function) => {
    dispatch(setSavingProgram(true));
    dispatch(setSavedProgram(false));
    clientService.updateClientProgram(program).subscribe({
      next: (newProgram) => {
        dispatch(uiSlice.setSuccessMessage('snackbar.updateProgramSuccess'));
        dispatch(updateProgram(newProgram));
        dispatch(setSavingProgram(false));
        dispatch(setSavedProgram(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingProgram(false));
        dispatch(setSavedProgram(false));
      },
    });
  };

export const deleteClientProgramAsync =
  (clientBC: string, program: Program) => (dispatch: Function) => {
    dispatch(setSavingProgram(true));
    dispatch(setSavedProgram(false));
    clientService
      .deleteClientProgram(clientBC, program.programUUID!)
      .subscribe({
        next: () => {
          dispatch(uiSlice.setSuccessMessage('snackbar.deleteProgramSuccess'));
          dispatch(deleteProgram(program));
          dispatch(setSavingProgram(false));
          dispatch(setSavedProgram(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingProgram(false));
          dispatch(setSavedProgram(false));
        },
      });
  };

export const updateProgramEffectiveDatesAsync =
  (
    clientBC: string,
    programUUID: string,
    effectiveStartDate?: string,
    effectiveEndDate?: string,
  ) =>
  (dispatch: Function) => {
    dispatch(setSavingProgram(true));
    dispatch(setSavedProgram(false));
    clientService
      .updateProgramEffectiveDates(
        clientBC,
        programUUID,
        effectiveStartDate,
        effectiveEndDate,
      )
      .subscribe({
        next: (newProgram) => {
          dispatch(
            uiSlice.setSuccessMessage('snackbar.activateProgramSuccess'),
          );
          dispatch(updateProgram(newProgram));
          dispatch(setSavingProgram(false));
          dispatch(setSavedProgram(true));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
          dispatch(setSavingProgram(false));
          dispatch(setSavedProgram(false));
        },
      });
  };

// Passenger Client Data
export const fetchPassengerClientDataAsync =
  (clientBC: string, servicePlans: { servicePlanUUID: string }[]) =>
  (dispatch: Function) => {
    dispatch(setLoadingPassengerClientData(true));
    dispatch(setLoadedPassengerClientData(false));
    dispatch(setPassengerClientData({ purposeCodes: [], servicePlans: [] }));
    clientService.getPassengerClientData(clientBC, servicePlans).subscribe({
      next: (client) => {
        dispatch(setPassengerClientData(client));
        dispatch(setLoadingPassengerClientData(false));
        dispatch(setLoadedPassengerClientData(true));
        dispatch(fetchClientProgramsAsync(clientBC));
      },
      error: (err) => {
        dispatch(setLoadingPassengerClientData(false));
        dispatch(setLoadedPassengerClientData(false));
      },
    });
  };

// Transportation benefit models
export const fetchTransportationBenefitModelsAsync =
  (states?: TransportationBenefitModelState[]) => (dispatch: Function) => {
    dispatch(setLoadingTransportationBenefitModels(true));
    dispatch(setLoadedTransportationBenefitModels(false));
    clientService.getTransportationBenefitModels(states).subscribe({
      next: (allTransportationBenefitModels) => {
        dispatch(
          setTransportationBenefitModels(allTransportationBenefitModels),
        );
        dispatch(setLoadingTransportationBenefitModels(false));
        dispatch(setLoadedTransportationBenefitModels(true));
      },
      error: (err) => {
        dispatch(setLoadingTransportationBenefitModels(false));
        dispatch(setLoadedTransportationBenefitModels(false));
      },
    });
  };

export const createTransportationBenefitModelAsync =
  (tbm: TransportationBenefitModel) => (dispatch: Function) => {
    dispatch(setSavingTransportationBenefitModel(true));
    dispatch(setSavedTransportationBenefitModel(false));
    clientService.createTransportationBenefitModel(tbm).subscribe({
      next: (newTbm) => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.createTransBenifitModelSuccess'),
        );
        dispatch(createTransportationBenefitModel(newTbm));
        dispatch(setSavingTransportationBenefitModel(false));
        dispatch(setSavedTransportationBenefitModel(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingTransportationBenefitModel(false));
        dispatch(setSavedTransportationBenefitModel(false));
      },
    });
  };

export const updateTransportationBenefitModelAsync =
  (tbm: TransportationBenefitModel) => (dispatch: Function) => {
    dispatch(setSavingTransportationBenefitModel(true));
    dispatch(setSavedTransportationBenefitModel(false));
    clientService.updateTransportationBenefitModel(tbm).subscribe({
      next: (newTbm) => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.updateTransBenifitModelSuccess'),
        );
        dispatch(updateTransportationBenefitModel(newTbm));
        dispatch(setSavingTransportationBenefitModel(false));
        dispatch(setSavedTransportationBenefitModel(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingTransportationBenefitModel(false));
        dispatch(setSavedTransportationBenefitModel(false));
      },
    });
  };
export const deleteTransportationBenefitModelAsync =
  (tbm: TransportationBenefitModel) => (dispatch: Function) => {
    dispatch(setLoadingTransportationBenefitModels(true));
    clientService.deleteTransportationBenefitModel(tbm).subscribe({
      next: () => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.deleteTransBenifitModelSuccess'),
        );
        dispatch(deleteTransportationBenefitModel(tbm));
        dispatch(setLoadingTransportationBenefitModels(false));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setLoadingTransportationBenefitModels(false));
      },
    });
  };

export const activateTransportationBenefitModelAsync =
  (tbm: TransportationBenefitModel) => (dispatch: Function) => {
    dispatch(setLoadingTransportationBenefitModels(true));
    clientService.activateTransportationBenefitModel(tbm).subscribe({
      next: (tbm) => {
        dispatch(
          uiSlice.setSuccessMessage(
            'snackbar.activateTransBenifitModelSuccess',
          ),
        );
        dispatch(updateTransportationBenefitModel(tbm));
        dispatch(setLoadingTransportationBenefitModels(false));
        dispatch(setCurrentClient(null)); // Reset current client to clear cached transportation benefits
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setLoadingTransportationBenefitModels(false));
      },
    });
  };

export const deactivateTransportationBenefitModelAsync =
  (tbm: TransportationBenefitModel) => (dispatch: Function) => {
    dispatch(setLoadingTransportationBenefitModels(true));
    clientService.deactivateTransportationBenefitModel(tbm).subscribe({
      next: (tbm) => {
        dispatch(
          uiSlice.setSuccessMessage(
            'snackbar.deactivateTransBenifitModelSuccess',
          ),
        );
        dispatch(updateTransportationBenefitModel(tbm));
        dispatch(setLoadingTransportationBenefitModels(false));
        dispatch(setCurrentClient(null)); // Reset current client to clear cached transportation benefits
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setLoadingTransportationBenefitModels(false));
      },
    });
  };

// Transportation benefit functions
export const fetchTransportationBenefitFunctionsAsync =
  () => (dispatch: Function) => {
    dispatch(setLoadingTransportationBenefitFunctions(true));
    dispatch(setLoadedTransportationBenefitFunctions(false));
    clientService.getTransportationBenefitFunctions().subscribe({
      next: (transportationBenefitFunctions) => {
        dispatch(
          setTransportationBenefitFunctions(transportationBenefitFunctions),
        );
        dispatch(setLoadingTransportationBenefitFunctions(false));
        dispatch(setLoadedTransportationBenefitFunctions(true));
      },
      error: (err) => {
        dispatch(setLoadingTransportationBenefitFunctions(false));
        dispatch(setLoadedTransportationBenefitFunctions(false));
      },
    });
  };

// Client Profile Program Settings
export const deleteClientProgramSettingAsync =
  (programSettingUUID: string) => (dispatch: Function) => {
    dispatch(setSavingProgramSettings(true));
    dispatch(setSavedProgramSettings(false));
    clientService.deleteClientProgramSetting(programSettingUUID).subscribe({
      next: () => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.deleteProgramSettingSuccess'),
        );
        dispatch(deleteProgramSetting(programSettingUUID));
        dispatch(setSavingProgramSettings(false));
        dispatch(setSavedProgramSettings(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingProgramSettings(false));
        dispatch(setSavedProgramSettings(false));
      },
    });
  };

// Named Locations
export const fetchNamedLocationsByUUIDsAsync =
  (namedLocationsUUIDs: string[]) => (dispatch: Function) => {
    dispatch(setLoadingNamedLocations(true));
    dispatch(setLoadedNamedLocations(false));
    clientService.getNamedLocationsByUUID(namedLocationsUUIDs).subscribe({
      next: (namedLocations) => {
        dispatch(setNamedLocations(namedLocations));
        dispatch(setLoadingNamedLocations(false));
        dispatch(setLoadedNamedLocations(true));
      },
      error: (err) => {
        dispatch(setLoadingNamedLocations(false));
        dispatch(setLoadedNamedLocations(false));
      },
    });
  };

export const createNamedLocationAsync =
  (namedLocation: CreatableNamedLocation) => (dispatch: Function) => {
    dispatch(setSavingNamedLocation(true));
    dispatch(setSavedNamedLocation(false));
    clientService.createNamedLocation(namedLocation).subscribe({
      next: (newNamedLocation) => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.createNamedLocationSuccess'),
        );
        dispatch(createNamedLocation(newNamedLocation));
        dispatch(setSavingNamedLocation(false));
        dispatch(setSavedNamedLocation(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingNamedLocation(false));
        dispatch(setSavedNamedLocation(false));
      },
    });
  };

export const updateNamedLocationAsync =
  (namedLocation: EditableNamedLocation) => (dispatch: Function) => {
    dispatch(setSavingNamedLocation(true));
    dispatch(setSavedNamedLocation(false));
    clientService.updateNamedLocation(namedLocation).subscribe({
      next: (newNamedLocation) => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.updateNamedLocationSuccess'),
        );
        dispatch(updateNamedLocation(newNamedLocation));
        dispatch(setSavingNamedLocation(false));
        dispatch(setSavedNamedLocation(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingNamedLocation(false));
        dispatch(setSavedNamedLocation(false));
      },
    });
  };

export const deleteNamedLocationAsync =
  (namedLocationUUID: string) => (dispatch: Function) => {
    dispatch(setSavingNamedLocation(true));
    dispatch(setSavedNamedLocation(false));
    clientService.deleteNamedLocation(namedLocationUUID).subscribe({
      next: () => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.deleteNamedLocationSuccess'),
        );
        dispatch(deleteNamedLocation(namedLocationUUID));
        dispatch(setSavingNamedLocation(false));
        dispatch(setSavedNamedLocation(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingNamedLocation(false));
        dispatch(setSavedNamedLocation(false));
      },
    });
  };

// Client Locations
export const fetchClientLocationsAsync =
  (clientBC: string) => (dispatch: Function) => {
    dispatch(setLoadingClientLocations(true));
    dispatch(setLoadedClientLocations(false));
    clientService.getClientLocations(clientBC).subscribe({
      next: (clientLocations) => {
        dispatch(setClientLocations(clientLocations));
        dispatch(setLoadingClientLocations(false));
        dispatch(setLoadedClientLocations(true));
      },
      error: (err) => {
        dispatch(setLoadingClientLocations(false));
        dispatch(setLoadedClientLocations(false));
      },
    });
  };

export const createClientLocationAsync =
  (creatableNamedLocation: CreatableNamedLocation, payload: any) =>
  (dispatch: Function) => {
    dispatch(setSavingClientLocation(true));
    dispatch(setSavedClientLocation(false));
    clientService.createNamedLocation(creatableNamedLocation).subscribe({
      next: (newNamedLocation) => {
        dispatch(createNamedLocation(newNamedLocation));
        clientService
          .createClientLocation({
            ...payload,
            namedLocationUUID: newNamedLocation.namedLocationUUID,
          })
          .subscribe({
            next: (newClientLocation) => {
              dispatch(
                uiSlice.setSuccessMessage(
                  'snackbar.createClientLocationSuccess',
                ),
              );
              dispatch(createClientLocation(newClientLocation));
              dispatch(setSavingClientLocation(false));
              dispatch(setSavedClientLocation(true));
            },
            error: (err) => {
              dispatch(uiSlice.setErrorMessage(err.message));
              dispatch(setSavingClientLocation(false));
              dispatch(setSavedClientLocation(false));
            },
          });
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingClientLocation(false));
        dispatch(setSavedClientLocation(false));
      },
    });
  };

export const updateClientLocationAsync =
  (editableNamedLocation: EditableNamedLocation) => (dispatch: Function) => {
    dispatch(setSavingClientLocation(true));
    dispatch(setSavedClientLocation(false));
    clientService.updateNamedLocation(editableNamedLocation).subscribe({
      next: (newNamedLocation) => {
        dispatch(updateNamedLocation(newNamedLocation));
        dispatch(
          uiSlice.setSuccessMessage('snackbar.updateClientLocationSuccess'),
        );
        dispatch(setSavingClientLocation(false));
        dispatch(setSavedClientLocation(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingClientLocation(false));
        dispatch(setSavedClientLocation(false));
      },
    });
  };

export const deleteClientLocationAsync =
  (payload: DeleteClientLocationRequest & DeleteNamedLocationRequest) =>
  (dispatch: Function) => {
    dispatch(setSavingClientLocation(true));
    dispatch(setSavedClientLocation(false));
    const { namedLocationUUID, ...clientLocationPayload } = payload;
    clientService.deleteClientLocation(clientLocationPayload).subscribe({
      next: () => {
        dispatch(
          deleteClientLocation(clientLocationPayload.clientLocationUUID),
        );
        clientService.deleteNamedLocation(namedLocationUUID).subscribe({
          next: () => {
            dispatch(deleteNamedLocation(namedLocationUUID));
            dispatch(
              uiSlice.setSuccessMessage('snackbar.deleteClientLocationSuccess'),
            );
            dispatch(setSavingClientLocation(false));
            dispatch(setSavedClientLocation(true));
          },
          error: (err) => {
            dispatch(uiSlice.setErrorMessage(err.message));
            dispatch(setSavingClientLocation(false));
            dispatch(setSavedClientLocation(false));
          },
        });
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingClientLocation(false));
        dispatch(setSavedClientLocation(false));
      },
    });
  };

// Client Preferences
export const fetchClientSettingsAsync =
  (clientBC: string) => (dispatch: Function) => {
    dispatch(setLoadingClientSettings(true));
    dispatch(setLoadedClientSettings(false));
    clientService.getClientSettings(clientBC).subscribe({
      next: (prefs) => {
        dispatch(setClientSettings(prefs));
        dispatch(setLoadingClientSettings(false));
        dispatch(setLoadedClientSettings(true));
      },
      error: (err) => {
        dispatch(setLoadingClientSettings(false));
        dispatch(setLoadedClientSettings(false));
      },
    });
  };
export const saveClientSettingsAsync =
  (clientBC: string, prefs: ClientSetting[]) => (dispatch: Function) => {
    dispatch(setLoadingClientSettings(true));
    dispatch(setLoadedClientSettings(false));
    clientService.saveClientSettings(clientBC, prefs).subscribe({
      next: () => {
        dispatch(
          uiSlice.setSuccessMessage('snackbar.saveClientSettingsSuccess'),
        );
        dispatch(setClientSettings(prefs));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage('snackbar.saveClientSettingsFailed'));
      },
    });
  };

const createPurposeCodeRequests = (purposeCodes: ClientPurposeCode[]) => {
  return purposeCodes.map((pc) => {
    if (pc.purposeCodeUUID) {
      if (pc.purposeCodeCategoryBC) {
        return clientService.updateClientPurposeCode(pc);
      } else {
        return clientService.deleteClientPurposeCode(
          pc.clientBC,
          pc.purposeCodeUUID!,
        );
      }
    } else {
      return clientService.createClientPurposeCode(pc);
    }
  });
};

const createBenefitRequests = (benefits: ClientTransportationBenefit[]) => {
  // TODO: add this when the back end is ready
  return [];
};

const createServiceRequests = (benefits: ClientTransporationService[]) => {
  // TODO: add this when the back end is ready
  return [];
};

export const saveClientModelsAsync =
  (
    clientBC: string,
    purposeCodes: ClientPurposeCode[],
    benefits: ClientTransportationBenefit[],
    services: ClientTransporationService[],
  ) =>
  (dispatch: Function) => {
    dispatch(setSavingClientModels(true));
    dispatch(setSavedClientModels(false));
    const purposeCodeRequests = createPurposeCodeRequests(purposeCodes);
    const benefitRequests = createBenefitRequests(benefits);
    const serviceRequests = createServiceRequests(services);
    forkJoin([
      ...purposeCodeRequests,
      ...benefitRequests,
      ...serviceRequests,
    ]).subscribe({
      next: (results) => {
        dispatch(uiSlice.setSuccessMessage('snackbar.saveClientModelsSuccess'));
        dispatch(setSavingClientModels(false));
        dispatch(setSavedClientModels(true));
        dispatch(fetchClientPurposeCodesAsync(clientBC));
      },
      error: (errors) => {
        dispatch(uiSlice.setErrorMessage('snackbar.saveClientModelsFailed'));
        dispatch(setSavingClientModels(false));
        dispatch(setSavedClientModels(false));
      },
    });
  };

export const fetchClientAdminSettingsAsync =
  (clientBC: string) => (dispatch: Function) => {
    clientService.getClientAdminSettings(clientBC).subscribe({
      next: (settings) => {
        dispatch(setClientAdminSettings(settings));
      },
      error: (errors) => {
        dispatch(
          uiSlice.setErrorMessage('snackbar.fetchClientAdminSettingsError'),
        );
      },
    });
  };

export const updateClientAdminSettingsAsync =
  (settings: ClientAdminSettings) => (dispatch: Function) => {
    clientService.updateClientAdminSettings(settings).subscribe({
      next: (settings) => {
        dispatch(
          uiSlice.setSuccessMessage(
            'snackbar.updateClientAdminSettingsSuccess',
          ),
        );
        dispatch(setClientAdminSettings(settings));
      },
      error: (err) => {
        dispatch(
          uiSlice.setErrorMessage('snackbar.updateClientAdminSettingsFailed'),
        );
      },
    });
  };

// Clients List
export const selectClientsList = (state: { client: ClientState }) =>
  state.client.clientsList;
export const selectLoadingClientsList = (state: { client: ClientState }) =>
  state.client.loadingClientsList;
export const selectLoadedClientsList = (state: { client: ClientState }) =>
  state.client.loadedClientsList;

// Clients
export const selectClients = (state: { client: ClientState }) =>
  state.client.clients;
export const selectLoadingClients = (state: { client: ClientState }) =>
  state.client.loadingClients;
export const selectLoadedClients = (state: { client: ClientState }) =>
  state.client.loadedClients;
export const selectCurrentClient = (state: { client: ClientState }) =>
  state.client.currentClient;
export const selectLoadingCurrentClient = (state: { client: ClientState }) =>
  state.client.loadingCurrentClient;
export const selectLoadedCurrentClient = (state: { client: ClientState }) =>
  state.client.loadedCurrentClient;
export const selectSavingClient = (state: { client: ClientState }) =>
  state.client.savingClient;
export const selectSavedClient = (state: { client: ClientState }) =>
  state.client.savedClient;

// Addresses
export const selectAddresses = (state: { client: ClientState }) =>
  state.client.currentClientAddresses;
export const selectLoadingAddresses = (state: { client: ClientState }) =>
  state.client.loadingAddresses;
export const selectLoadedAddresses = (state: { client: ClientState }) =>
  state.client.loadedAddresses;
export const selectSavingAddress = (state: { client: ClientState }) =>
  state.client.savingAddress;
export const selectSavedAddress = (state: { client: ClientState }) =>
  state.client.savedAddress;

// Contacts
export const selectContacts = (state: { client: ClientState }) =>
  state.client.currentClientContacts;
export const selectLoadingContacts = (state: { client: ClientState }) =>
  state.client.loadingContacts;
export const selectLoadedContacts = (state: { client: ClientState }) =>
  state.client.loadedContacts;
export const selectSavingContact = (state: { client: ClientState }) =>
  state.client.savingContact;
export const selectSavedContact = (state: { client: ClientState }) =>
  state.client.savedContact;

// Member contact (maps keyed by `memberContactKey(clientBC, servicePlanID)`)
export const selectMemberContactByClientPlan = (state: {
  client: ClientState;
}) => state.client.memberContactByClientPlan;
export const selectLoadingMemberContactByClientPlan = (state: {
  client: ClientState;
}) => state.client.loadingMemberContactByClientPlan;

export const selectAdmins = (state: { client: ClientState }) =>
  state.client.currentClientAdmins;
export const selectLoadingAdmins = (state: { client: ClientState }) =>
  state.client.loadingAdmins;
export const selectLoadedAdmins = (state: { client: ClientState }) =>
  state.client.loadedAdmins;
export const selectSavingAdmin = (state: { client: ClientState }) =>
  state.client.savingAdmin;
export const selectSavedAdmin = (state: { client: ClientState }) =>
  state.client.savedAdmin;
export const selectAdminError = (state: { client: ClientState }) =>
  state.client.adminError;

// Purpose codes
export const selectPurposeCodes = (state: { client: ClientState }) =>
  state.client.currentClientPurposeCodes;
export const selectLoadingPurposeCodes = (state: { client: ClientState }) =>
  state.client.loadingPurposeCodes;
export const selectLoadedPurposeCodes = (state: { client: ClientState }) =>
  state.client.loadedPurposeCodes;
export const selectSavingPurposeCode = (state: { client: ClientState }) =>
  state.client.savingPurposeCode;
export const selectSavedPurposeCode = (state: { client: ClientState }) =>
  state.client.savedPurposeCode;

// Service areas
export const selectServiceAreas = (state: { client: ClientState }) =>
  state.client.currentClientServiceAreas;
export const selectLoadingServiceAreas = (state: { client: ClientState }) =>
  state.client.loadingServiceAreas;
export const selectLoadedServiceAreas = (state: { client: ClientState }) =>
  state.client.loadedServiceAreas;
export const selectSavingServiceArea = (state: { client: ClientState }) =>
  state.client.savingServiceArea;
export const selectSavedServiceArea = (state: { client: ClientState }) =>
  state.client.savedServiceArea;

// Transportation benefits
export const selectTransportationBenefits = (state: { client: ClientState }) =>
  state.client?.currentTransportationBenefits;
export const selectLoadingTransportationBenefits = (state: {
  client: ClientState;
}) => state.client.loadingTransportationBenefits;
export const selectLoadedTransportationBenefits = (state: {
  client: ClientState;
}) => state.client.loadedTransportationBenefits;
export const selectSavingTransportationBenefit = (state: {
  client: ClientState;
}) => state.client.savingTransportationBenefit;
export const selectSavedTransportationBenefit = (state: {
  client: ClientState;
}) => state.client.savedTransportationBenefit;

// Service plans
export const selectServicePlanList = (state: { client: ClientState }) =>
  state.client.servicePlanList;
export const selectManualServicePlanList = (state: { client: ClientState }) =>
  state.client.manualServicePlanList;
export const selectLoadingServicePlans = (state: { client: ClientState }) =>
  state.client.loadingServicePlans;
export const selectLoadedServicePlans = (state: { client: ClientState }) =>
  state.client.loadedServicePlans;
export const selectSavingServicePlan = (state: { client: ClientState }) =>
  state.client.savingServicePlan;
export const selectSavedServicePlan = (state: { client: ClientState }) =>
  state.client.savedServicePlan;

// Programs
export const selectPrograms = (state: { client: ClientState }) =>
  state.client.programs;
export const selectLoadingPrograms = (state: { client: ClientState }) =>
  state.client.loadingPrograms;
export const selectLoadedPrograms = (state: { client: ClientState }) =>
  state.client.loadedPrograms;
export const selectSavingProgram = (state: { client: ClientState }) =>
  state.client.savingProgram;
export const selectSavedProgram = (state: { client: ClientState }) =>
  state.client.savedProgram;

// Passenger Client Data
export const selectPassengerClientData = (state: { client: ClientState }) =>
  state.client.passengerClientData;
export const selectLoadingPassengerClientData = (state: {
  client: ClientState;
}) => state.client.loadingPassengerClientData;
export const selectLoadedPassengerClientData = (state: {
  client: ClientState;
}) => state.client.loadedPassengerClientData;

// Transportation benefit models
export const selectAllTransportationBenefitModels = (state: {
  client: ClientState;
}) => state.client.allTransportationBenefitModels;
export const selectActiveTransportationBenefitModels = (state: {
  client: ClientState;
}) => state.client.activeTransportationBenefitModels;
export const selectLoadingTransportationBenefitModels = (state: {
  client: ClientState;
}) => state.client.loadingTransportationBenefitModels;
export const selectLoadedTransportationBenefitModels = (state: {
  client: ClientState;
}) => state.client.loadedTransportationBenefitModels;
export const selectSavingTransportationBenefitModel = (state: {
  client: ClientState;
}) => state.client.savingTransportationBenefitModel;
export const selectSavedTransportationBenefitModel = (state: {
  client: ClientState;
}) => state.client.savedTransportationBenefitModel;

// Transportation benefit functions
export const selectTransportationBenefitFunctions = (state: {
  client: ClientState;
}) => state.client.transportationBenefitFunctions;
export const selectLoadingTransportationBenefitFunctions = (state: {
  client: ClientState;
}) => state.client.loadingTransportationBenefitFunctions;
export const selectLoadedTransportationBenefitFunctions = (state: {
  client: ClientState;
}) => state.client.loadedTransportationBenefitFunctions;

// Client Profile Program Settings
export const selectProgramSettings = (state: { client: ClientState }) =>
  state.client.programSettingsById;

// Named Locations
export const selectNamedLocations = (state: { client: ClientState }) =>
  state.client.allNamedLocations;
export const selectLoadingNamedLocations = (state: { client: ClientState }) =>
  state.client.loadingNamedLocations;
export const selectLoadedNamedLocations = (state: { client: ClientState }) =>
  state.client.loadedNamedLocations;
export const selectSavingNamedLocation = (state: { client: ClientState }) =>
  state.client.savingNamedLocation;
export const selectSavedNamedLocation = (state: { client: ClientState }) =>
  state.client.savedNamedLocation;

// Client Locations
export const selectClientLocations = (state: { client: ClientState }) =>
  state.client.allClientLocations;
export const selectLoadingClientLocations = (state: { client: ClientState }) =>
  state.client.loadingClientLocations;
export const selectLoadedClientLocations = (state: { client: ClientState }) =>
  state.client.loadedClientLocations;
export const selectSavingClientLocation = (state: { client: ClientState }) =>
  state.client.savingClientLocation;
export const selectSavedClientLocation = (state: { client: ClientState }) =>
  state.client.savedClientLocation;

// Client Preferences
export const selectClientSettings = (state: { client: ClientState }) =>
  state.client.clientSettings;
export const selectLoadingClientSettings = (state: { client: ClientState }) =>
  state.client.loadingClientSettings;
export const selectLoadedClientSettings = (state: { client: ClientState }) =>
  state.client.loadedClientSettings;
export const selectSavingClientSettings = (state: { client: ClientState }) =>
  state.client.savingClientSettings;
export const selectSavedClientSettings = (state: { client: ClientState }) =>
  state.client.savedClientSettings;

// Client Models
export const selectSavingClientModels = (state: { client: ClientState }) =>
  state.client.savingClientModels;
export const selectSavedClientModels = (state: { client: ClientState }) =>
  state.client.savedClientModels;

// Client Admin Settings
export const selectClientAdminSettings = (state: { client: ClientState }) =>
  state.client.clientAdminSettings;

export default slice.reducer;
